import { A as OUTPUT_RESERVE_RATIO, B as truncationNotice, C as mcpToolPolicyStateLabel, D as COMPACTING_LABEL, E as CHARS_PER_TOKEN, F as estimateTokens, H as DeviceLoginError, I as manualCompactionNotice, J as AuthenticationError, K as sleep, L as modelContextBudget, M as contextAfterSummary, N as contextMeter, O as DEFAULT_COMPACTION_THRESHOLD, P as contextTokenBudget, R as summaryDescription, S as mcpToolPolicyState, T as waitForMcpConnection, U as OPENHARNESS_CLI_CLIENT_ID, V as isEventList, W as createAuthResource, X as apiErrorFromResponse, Y as ResponseValidationError, Z as errorTypeForStatus, _ as mcpStatusLabel, a as TOOL_POLICIES, b as mcpToolEntriesByServer, c as mcpAuthLabel, d as mcpServerById, f as mcpServerByName, g as mcpStatusDetail, h as mcpServerTools, i as MCP_CONNECT_TIMEOUT_MS, j as compactionThreshold, k as DEFAULT_CONTEXT_TOKEN_BUDGET, l as mcpConnectedServerId, m as mcpServerPolicyBody, n as MCP_CONNECTED_PARAM, o as estimatedTokensLabel, p as mcpServerFromError, q as ApiError, r as MCP_CONNECT_POLL_INTERVAL_MS, s as mcpAuthHint, t as MCP_AUTH_TYPES, u as mcpFailureNotice, v as mcpStatusTone, w as toolPolicyLabel, x as mcpToolPolicyBody, y as mcpTestOutcome, z as summaryModelFallback } from "./mcp-OaEFFRME.js";
import { AGENT_MESSAGE_TOOL_NAME, AGENT_STOP_TOOL_NAME, AGENT_TOOL_NAME, API_VERSION_PREFIX, ASK_USER_TOOL_NAME, AgentSchema, CREDENTIAL_TYPES, CompactSessionResponseSchema, ConnectMcpServerResponseSchema, EVENT_TYPES, GetMeResponseSchema, GetPreferencesResponseSchema, GetSkillResponseSchema, ImportSkillsResponseSchema, JSON_CONTENT_TYPE, LAST_EVENT_ID_HEADER, ListAgentsResponseSchema, ListEventsResponseSchema, ListMcpServersResponseSchema, ListModelsResponseSchema, ListModesResponseSchema, ListProviderCredentialsResponseSchema, ListSessionsResponseSchema, ListSkillsResponseSchema, ListToolSettingsResponseSchema, MAX_PAGE_LIMIT, MESSAGE_PARENT_TOOL_NAME, McpServerSchema, ModeSchema, PROVIDERS as PROVIDERS$1, PreviewSkillImportResponseSchema, ProviderCredentialSchema, REQUEST_ID_HEADER, RefreshSkillPreviewResponseSchema, SSE_CONTENT_TYPE, SendEventsResponseSchema, SessionSchema, SessionUsageSchema, SkillFileContentSchema, SkillSchema, StreamEventSchema, TASK_WAIT_TOOL_NAME, TODO_WRITE_TOOL_NAME, UserUsageSchema, WEB_SEARCH_TOOL_NAME, askUserAnswerProblems, bedrockRegionOf, isStoredEvent, parseAskUserInput, readTodoList, skillSettingsOf, skillsInForce, toolCallId, toolCallInput, toolCallOfferedName, toolCallPermission, toolCallServer, toolCallSource, toolResultCallId, toolResultIsError, totalCost, usageCost } from "@openharness/protocol";
//#region src/http.ts
/** Build the transport a client uses for every request. */
function createTransport(options) {
	const baseUrl = stripTrailingSlashes(options.baseUrl);
	const fetchImpl = resolveFetch(options.fetch);
	const debug = options.debug ?? (() => {});
	/** Issue the request as written; the caller decides what a non-2xx status means. */
	async function fetchResponse(spec, accept) {
		const url = baseUrl + spec.path + queryString(spec.query);
		return fetchImpl(url, {
			method: spec.method,
			credentials: "include",
			headers: requestHeaders(options.token, spec, accept),
			...spec.body === void 0 ? {} : { body: JSON.stringify(spec.body) },
			...spec.signal === void 0 ? {} : { signal: spec.signal }
		});
	}
	async function send(spec, accept) {
		const response = await fetchResponse(spec, accept);
		if (!response.ok) {
			const body = await readJson(response);
			throw apiErrorFromResponse(response.status, body, {
				statusText: response.statusText,
				requestId: response.headers.get(REQUEST_ID_HEADER) ?? void 0
			});
		}
		return response;
	}
	return {
		debug,
		async json(schema, spec) {
			const response = await send(spec, JSON_CONTENT_TYPE);
			return parseBody(schema, await readJson(response), response.status);
		},
		async rawJson(spec) {
			const response = await fetchResponse(spec, JSON_CONTENT_TYPE);
			return {
				status: response.status,
				statusText: response.statusText,
				body: await readJson(response),
				requestId: response.headers.get(REQUEST_ID_HEADER) ?? void 0
			};
		},
		async noContent(spec) {
			await (await send(spec, JSON_CONTENT_TYPE)).text();
		},
		async openEventStream(spec) {
			const response = await send(spec, SSE_CONTENT_TYPE);
			const contentType = response.headers.get("content-type");
			if (contentType !== null && !contentType.toLowerCase().includes(SSE_CONTENT_TYPE)) throw new ResponseValidationError(response.status, `The server answered with "${contentType}" instead of an event stream.`);
			if (response.body === null) throw new ResponseValidationError(response.status, "The server opened an event stream with an empty body.");
			return response.body;
		}
	};
}
/** Parse a response body against a schema, or throw {@link ResponseValidationError}. */
function parseBody(schema, body, status) {
	const parsed = schema.safeParse(body);
	if (!parsed.success) throw new ResponseValidationError(status, "The server returned a response that does not match the protocol.", parsed.error.message);
	return parsed.data;
}
/** The headers every request carries, plus the ones only the event stream uses. */
function requestHeaders(token, spec, accept) {
	const headers = { accept };
	if (spec.body !== void 0) headers["content-type"] = JSON_CONTENT_TYPE;
	if (token !== void 0) headers.authorization = `Bearer ${token}`;
	if (spec.lastEventId !== void 0) headers[LAST_EVENT_ID_HEADER] = String(spec.lastEventId);
	return headers;
}
/**
* Write query parameters the way the protocol spells them: `types[]=a&types[]=b`.
*
* The key is written verbatim — the array spelling lives in the key, and the keys are
* constants in this package, never caller input — while values are percent-encoded. A
* `URLSearchParams` would encode the brackets too; both spellings mean the same thing to a
* server, and the literal one is what the protocol's documentation shows.
*/
function queryString(query) {
	if (query === void 0) return "";
	const parts = [];
	for (const [key, value] of Object.entries(query)) {
		if (value === void 0) continue;
		for (const item of queryValues(value)) parts.push(`${key}=${encodeURIComponent(String(item))}`);
	}
	return parts.length === 0 ? "" : `?${parts.join("&")}`;
}
/** A query value as the list of strings to write, one `key=value` pair each. */
function queryValues(value) {
	return isQueryArray(value) ? value : [value];
}
/** `Array.isArray` as a type guard, so the narrowed element type survives. */
function isQueryArray(value) {
	return Array.isArray(value);
}
/** The body as parsed JSON, or `undefined` when the response had no body to parse. */
async function readJson(response) {
	const text = await response.text();
	if (text === "") return;
	try {
		return JSON.parse(text);
	} catch {
		return;
	}
}
/** Strip trailing slashes so a base URL and a path can be concatenated as they are. */
function stripTrailingSlashes(baseUrl) {
	return baseUrl.replace(/\/+$/, "");
}
/**
* The `fetch` to call: the caller's, or the global one, bound to `globalThis`.
*
* Browsers refuse a `fetch` called with the wrong receiver ("Illegal invocation"), and the
* global is a plain function value here, so the default goes through a wrapper rather than
* passing `globalThis.fetch` around.
*/
function resolveFetch(fetchImpl) {
	if (fetchImpl !== void 0) return fetchImpl;
	return (input, init) => globalThis.fetch(input, init);
}
//#endregion
//#region src/resources/agents.ts
/** Build the agents resource over a transport. */
function createAgentsResource(transport) {
	const path = `${API_VERSION_PREFIX}/agents`;
	return {
		create(body, options) {
			return transport.json(AgentSchema, {
				method: "POST",
				path,
				body,
				signal: options?.signal
			});
		},
		get(agentId, options) {
			return transport.json(AgentSchema, {
				method: "GET",
				path: `${path}/${agentId}`,
				signal: options?.signal
			});
		},
		list(params, options) {
			return transport.json(ListAgentsResponseSchema, {
				method: "GET",
				path,
				query: {
					limit: params?.limit,
					page: params?.page
				},
				signal: options?.signal
			});
		},
		update(agentId, body, options) {
			return transport.json(AgentSchema, {
				method: "POST",
				path: `${path}/${agentId}`,
				body,
				signal: options?.signal
			});
		}
	};
}
//#endregion
//#region src/resources/mcp-servers.ts
/** Build the MCP servers resource over a transport. */
function createMcpServersResource(transport) {
	const path = `${API_VERSION_PREFIX}/me/mcp_servers`;
	return {
		create(body, options) {
			return transport.json(McpServerSchema, {
				method: "POST",
				path,
				body,
				signal: options?.signal
			});
		},
		list(options) {
			return transport.json(ListMcpServersResponseSchema, {
				method: "GET",
				path,
				signal: options?.signal
			});
		},
		get(serverId, options) {
			return transport.json(McpServerSchema, {
				method: "GET",
				path: `${path}/${serverId}`,
				signal: options?.signal
			});
		},
		update(serverId, body, options) {
			return transport.json(McpServerSchema, {
				method: "POST",
				path: `${path}/${serverId}`,
				body,
				signal: options?.signal
			});
		},
		delete(serverId, options) {
			return transport.noContent({
				method: "DELETE",
				path: `${path}/${serverId}`,
				signal: options?.signal
			});
		},
		test(serverId, options) {
			return transport.json(McpServerSchema, {
				method: "POST",
				path: `${path}/${serverId}/test`,
				signal: options?.signal
			});
		},
		connect(serverId, body, options) {
			return transport.json(ConnectMcpServerResponseSchema, {
				method: "POST",
				path: `${path}/${serverId}/connect`,
				body: body ?? {},
				signal: options?.signal
			});
		},
		disconnect(serverId, options) {
			return transport.json(McpServerSchema, {
				method: "POST",
				path: `${path}/${serverId}/disconnect`,
				signal: options?.signal
			});
		}
	};
}
//#endregion
//#region src/resources/modes.ts
/** Build the modes resource over a transport. */
function createModesResource(transport) {
	const path = `${API_VERSION_PREFIX}/me/modes`;
	return {
		create(body, options) {
			return transport.json(ModeSchema, {
				method: "POST",
				path,
				body,
				signal: options?.signal
			});
		},
		get(modeId, options) {
			return transport.json(ModeSchema, {
				method: "GET",
				path: `${path}/${modeId}`,
				signal: options?.signal
			});
		},
		list(options) {
			return transport.json(ListModesResponseSchema, {
				method: "GET",
				path,
				signal: options?.signal
			});
		},
		update(modeId, body, options) {
			return transport.json(ModeSchema, {
				method: "POST",
				path: `${path}/${modeId}`,
				body,
				signal: options?.signal
			});
		},
		delete(modeId, options) {
			return transport.noContent({
				method: "DELETE",
				path: `${path}/${modeId}`,
				signal: options?.signal
			});
		}
	};
}
//#endregion
//#region src/resources/models.ts
/** Build the models resource over a transport. */
function createModelsResource(transport) {
	return { list(params, options) {
		return transport.json(ListModelsResponseSchema, {
			method: "GET",
			path: `${API_VERSION_PREFIX}/models`,
			query: params?.refresh === true ? { refresh: true } : void 0,
			signal: options?.signal
		});
	} };
}
//#endregion
//#region src/resources/preferences.ts
/** Build the preferences resource over a transport. */
function createPreferencesResource(transport) {
	const path = `${API_VERSION_PREFIX}/me/preferences`;
	return {
		get(options) {
			return transport.json(GetPreferencesResponseSchema, {
				method: "GET",
				path,
				signal: options?.signal
			});
		},
		put(preferences, options) {
			return transport.json(GetPreferencesResponseSchema, {
				method: "PUT",
				path,
				body: preferences,
				signal: options?.signal
			});
		}
	};
}
//#endregion
//#region src/resources/provider-credentials.ts
/** Build the provider-credentials resource over a transport. */
function createProviderCredentialsResource(transport) {
	const path = `${API_VERSION_PREFIX}/provider-credentials`;
	const credentialPath = (name) => `${path}/${encodeURIComponent(name)}`;
	return {
		list(options) {
			return transport.json(ListProviderCredentialsResponseSchema, {
				method: "GET",
				path,
				signal: options?.signal
			});
		},
		put(name, body, options) {
			return transport.json(ProviderCredentialSchema, {
				method: "PUT",
				path: credentialPath(name),
				body,
				signal: options?.signal
			});
		},
		delete(name, options) {
			return transport.noContent({
				method: "DELETE",
				path: credentialPath(name),
				signal: options?.signal
			});
		}
	};
}
//#endregion
//#region src/events/sse.ts
/**
* Parse an SSE byte stream into messages.
*
* Lines are assembled across chunk boundaries, so a message may arrive in any number of
* chunks and a field may be split anywhere — including inside a `\r\n`, which is why a
* trailing `\r` is held back until the next chunk can disambiguate it.
*
* Nothing is dispatched at the end of the stream: per the spec an event is only delivered by
* its terminating blank line, so a connection that dies mid-message leaves no half-event
* behind. What the message contained is then re-sent on the next connection anyway, because
* the client resumes from the last `seq` it saw.
*
* @param body the response body of an event-stream request
* @returns every complete message, in arrival order
*/
async function* parseSseStream(body) {
	const reader = body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	let firstChunk = true;
	const state = new SseMessageState();
	try {
		for (;;) {
			const { done, value } = await reader.read();
			if (done) {
				for (;;) {
					const line = takeLine(buffer, true);
					if (line === null) break;
					buffer = line.rest;
					const message = state.consumeLine(line.line);
					if (message !== null) yield message;
				}
				break;
			}
			buffer += decoder.decode(value, { stream: true });
			if (firstChunk) {
				buffer = buffer.replace(/^\uFEFF/, "");
				firstChunk = false;
			}
			for (;;) {
				const line = takeLine(buffer, false);
				if (line === null) break;
				buffer = line.rest;
				const message = state.consumeLine(line.line);
				if (message !== null) yield message;
			}
		}
	} finally {
		try {
			await reader.cancel();
		} catch {}
	}
}
/**
* Split the first complete line off `buffer`, or `null` when it holds no complete line yet.
*
* @param buffer the text read so far
* @param atEof whether the stream has ended, in which case a trailing `\r` does terminate the
*   line it ends instead of waiting for a `\n` that can no longer arrive
*/
function takeLine(buffer, atEof) {
	for (let index = 0; index < buffer.length; index += 1) {
		const code = buffer.charCodeAt(index);
		if (code === 10) return {
			line: buffer.slice(0, index),
			rest: buffer.slice(index + 1)
		};
		if (code === 13) {
			if (index === buffer.length - 1 && !atEof) return null;
			const skip = buffer.charCodeAt(index + 1) === 10 ? 2 : 1;
			return {
				line: buffer.slice(0, index),
				rest: buffer.slice(index + skip)
			};
		}
	}
	return null;
}
/**
* The field buffer of one message, per the EventSource processing model.
*
* A message is assembled from `field: value` lines and dispatched by a blank line. Unknown
* fields (`retry`, anything else) are recorded and ignored, which is what the spec asks for.
*/
var SseMessageState = class {
	#data = [];
	#event = null;
	#id = null;
	/**
	* Feed one line.
	*
	* @param line a line with its terminator removed
	* @returns the message to dispatch, or `null` when the line did not complete one
	*/
	consumeLine(line) {
		if (line === "") return this.#dispatch();
		if (line.startsWith(":")) return null;
		const { field, value } = splitField(line);
		if (field === "data") this.#data.push(value);
		else if (field === "event") this.#event = value;
		else if (field === "id") {
			if (!value.includes("\0")) this.#id = value;
		}
		return null;
	}
	/** Hand out the buffered message, if it has data, and start a new one. */
	#dispatch() {
		if (this.#data.length === 0) {
			this.#event = null;
			this.#id = null;
			return null;
		}
		const message = {
			id: this.#id,
			event: this.#event,
			data: this.#data.join("\n")
		};
		this.#data = [];
		this.#event = null;
		this.#id = null;
		return message;
	}
};
/**
* Split `name: value` at the first colon; a line without one is a field with an empty value,
* and exactly one space after the colon is part of the syntax rather than the value.
*/
function splitField(line) {
	const colon = line.indexOf(":");
	if (colon === -1) return {
		field: line,
		value: ""
	};
	const value = line.slice(colon + 1);
	return {
		field: line.slice(0, colon),
		value: value.startsWith(" ") ? value.slice(1) : value
	};
}
//#endregion
//#region src/events/stream.ts
/**
* Following a session's event stream.
*
* The stream is the live half of the session log: stored events arrive as they are written,
* and — if the connection asked for them — the chunks of a reply arrive while its
* `agent.message` is still being generated. A chunk comes in one of two forms, and `seq` tells
* them apart: the stored form (D9) is an `event_start` / `event_delta` with an `id` and a
* `seq`, delivered like any other stored event — so a client that drops mid-reply resumes
* mid-reply — and the stream-only preview has no envelope, is never replayed, and is a
* display aid for the connection that asked for it.
*
* A stream is long-lived and the network is not, so this is a reconnect loop rather than a
* single request. What makes it safe is the `seq` on every stored event:
*
* 1. the client remembers the last `seq` it delivered;
* 2. when the connection drops it reconnects with `last-event-id: <seq>` *and*
*    `after_seq=<seq>`, so the server resumes exactly there — the client does not have to
*    care which one the server honors;
* 3. anything at or below that `seq` is dropped before it reaches the caller.
*
* Reconnects back off exponentially (with jitter), so a server that is down is not hammered.
* Previews are never replayed — they were a display aid, and the stored `agent.message` that
* replaces a preview arrives either way — so a preview in flight when the connection dies is
* simply cut short; the transcript reducer handles that by keeping the partial preview until
* the stored event replaces it.
*
* One event ends the iteration instead of reconnecting (#111): `session.deleted`, the
* stream-only last event of a deleted session. It is delivered, and then the loop returns
* quietly — a session that is gone can never answer again, so a reconnect could only churn
* through 404s.
*/
/** How long to wait before the first reconnect; doubles per attempt. */
const BASE_RECONNECT_DELAY_MS = 500;
/** The ceiling for the reconnect delay. */
const MAX_RECONNECT_DELAY_MS = 15e3;
/** How much of the delay is jitter, either way, as a fraction. */
const JITTER_FRACTION = .25;
/**
* Follow a session's event stream, reconnecting as needed.
*
* The generator returns when `signal` aborts, and throws only for an answer that retrying
* cannot fix — an {@link ApiError} that is not retryable (a bad key, an unknown session) or a
* {@link ResponseValidationError}. Everything else (a dropped connection, a `5xx`, a `429`)
* is retried with backoff.
*
* @param transport the request layer
* @param sessionId the session to follow
* @param options see {@link StreamOptions}
*/
async function* followSessionEvents(transport, sessionId, options) {
	const signal = options.signal;
	let lastSeq = options.afterSeq;
	let attempt = 0;
	while (!isAborted(signal)) {
		try {
			const body = await transport.openEventStream({
				method: "GET",
				path: `${API_VERSION_PREFIX}/sessions/${sessionId}/events/stream`,
				query: {
					...options.deltas === true ? { "event_deltas[]": EVENT_TYPES.agentMessage } : {},
					...lastSeq === void 0 ? {} : { after_seq: lastSeq }
				},
				...signal === void 0 ? {} : { signal },
				...lastSeq === void 0 ? {} : { lastEventId: lastSeq }
			});
			attempt = 0;
			options.onConnection?.({ state: "connected" });
			for await (const message of parseSseStream(body)) {
				if (isAborted(signal)) return;
				const event = decodeStreamEvent(message.data, transport.debug);
				if (event === null) continue;
				if (!isStoredEvent(event)) {
					yield event;
					return;
				}
				if (lastSeq !== void 0 && event.seq <= lastSeq) continue;
				lastSeq = event.seq;
				yield event;
			}
			transport.debug("the event stream ended; reconnecting");
		} catch (error) {
			if (isAborted(signal)) return;
			if (!isRetryable(error)) throw error;
			transport.debug("the event stream disconnected; reconnecting", error);
		}
		if (isAborted(signal)) return;
		options.onConnection?.({
			state: "reconnecting",
			attempt
		});
		await sleep(reconnectDelayMs(attempt), signal);
		attempt += 1;
	}
}
/**
* Whether the caller has already aborted.
*
* A named function on purpose: TypeScript would otherwise carry its narrowing of
* `signal?.aborted` across the `await`s in the loop below, and consider the abort checks after
* them unreachable — but an abort happens exactly while the loop is suspended.
*/
function isAborted(signal) {
	return signal?.aborted === true;
}
/**
* The wait before reconnect attempt `attempt` (0-based): exponential from
* {@link BASE_RECONNECT_DELAY_MS} up to {@link MAX_RECONNECT_DELAY_MS}, plus or minus
* {@link JITTER_FRACTION} so that every client of a restarted server does not come back at
* the same instant.
*
* @param attempt how many consecutive reconnects have failed
*/
function reconnectDelayMs(attempt) {
	const exponential = Math.min(BASE_RECONNECT_DELAY_MS * 2 ** attempt, MAX_RECONNECT_DELAY_MS);
	const jitter = exponential * JITTER_FRACTION * (Math.random() * 2 - 1);
	return Math.max(0, Math.round(exponential + jitter));
}
/**
* Whether reconnecting can help: `fetch` failures (the request never got an answer) and the
* retryable HTTP statuses. Anything else is the server saying no, and retrying it forever
* would only hide the problem — a 401 in particular, which arrives as an
* {@link AuthenticationError}, is not retryable, so the loop stops on it and the caller can
* send the user to sign-in.
*/
function isRetryable(error) {
	if (error instanceof ApiError) return error.retryable;
	return !(error instanceof ResponseValidationError);
}
/**
* Parse one `data:` payload into a stream event.
*
* A payload this client cannot parse is dropped, not thrown: the event vocabulary is closed
* and grows, so an older client talking to a newer server will see event types it has never
* heard of. Dropping them keeps the rest of the stream usable, and the debug hook is where a
* caller finds out.
*
* @param data the message's `data` field
* @param debug hook for the skipped event
*/
function decodeStreamEvent(data, debug) {
	let value;
	try {
		value = JSON.parse(data);
	} catch {
		debug("skipping an event stream message that is not JSON", data);
		return null;
	}
	const parsed = StreamEventSchema.safeParse(value);
	if (!parsed.success) {
		debug(`skipping the stream event "${eventTypeOf(value)}": this client does not know it`, value);
		return null;
	}
	return parsed.data;
}
/** The `type` of a decoded payload, for the debug message; `unknown` when there is none. */
function eventTypeOf(value) {
	if (typeof value === "object" && value !== null && "type" in value) {
		const type = value.type;
		if (typeof type === "string") return type;
	}
	return "unknown";
}
//#endregion
//#region src/resources/sessions.ts
/** Build the sessions resource over a transport. */
function createSessionsResource(transport) {
	const path = `${API_VERSION_PREFIX}/sessions`;
	return {
		create(body, options) {
			return transport.json(SessionSchema, {
				method: "POST",
				path,
				body,
				signal: options?.signal
			});
		},
		get(sessionId, options) {
			return transport.json(SessionSchema, {
				method: "GET",
				path: sessionPath(sessionId),
				signal: options?.signal
			});
		},
		list(params, options) {
			return transport.json(ListSessionsResponseSchema, {
				method: "GET",
				path,
				query: {
					limit: params?.limit,
					page: params?.page,
					agent_id: params?.agent_id,
					parent_session_id: params?.parent_session_id
				},
				signal: options?.signal
			});
		},
		delete(sessionId, options) {
			return transport.noContent({
				method: "DELETE",
				path: sessionPath(sessionId),
				signal: options?.signal
			});
		},
		async compact(sessionId, options) {
			return (await transport.json(CompactSessionResponseSchema, {
				method: "POST",
				path: sessionCompactPath(sessionId),
				body: { ...options?.instructions === void 0 ? {} : { instructions: options.instructions } },
				signal: options?.signal
			})).data;
		},
		events: createSessionEventsResource(transport)
	};
}
/**
* The path of a session resource: `GET` here to read it, `DELETE` here to remove it.
*
* @param sessionId the `sesn_` id
*/
function sessionPath(sessionId) {
	return `${API_VERSION_PREFIX}/sessions/${sessionId}`;
}
/**
* The path of a session's event log: `POST` here to append, `GET` here to read.
*
* Exported for the helpers on the client, which send user events directly rather than through
* {@link SessionEventsResource.send} so that they can return the stored event itself.
*
* @param sessionId the `sesn_` id
*/
function sessionEventsPath(sessionId) {
	return `${sessionPath(sessionId)}/events`;
}
/**
* The path of a session's manual compaction: `POST` here to ask for one (#283).
*
* @param sessionId the `sesn_` id
*/
function sessionCompactPath(sessionId) {
	return `${sessionPath(sessionId)}/compact`;
}
/** Build the events sub-resource over a transport. */
function createSessionEventsResource(transport) {
	const eventsPath = sessionEventsPath;
	const list = (sessionId, params, options) => transport.json(ListEventsResponseSchema, {
		method: "GET",
		path: eventsPath(sessionId),
		query: {
			limit: params?.limit,
			order: params?.order,
			page: params?.page,
			"types[]": params?.types,
			after_seq: params?.after_seq
		},
		signal: options?.signal
	});
	return {
		send(sessionId, events, options) {
			return transport.json(SendEventsResponseSchema, {
				method: "POST",
				path: eventsPath(sessionId),
				body: { events: isEventList(events) ? events : [events] },
				signal: options?.signal
			});
		},
		list,
		async *iterate(sessionId, params, options) {
			const requested = /* @__PURE__ */ new Set();
			let page = params?.page;
			if (page !== void 0) requested.add(page);
			for (;;) {
				const response = await list(sessionId, {
					...params,
					page
				}, options);
				const next = response.next_page;
				if (next !== null && requested.has(next)) return;
				yield* response.data;
				if (next === null) return;
				requested.add(next);
				page = next;
			}
		},
		stream(sessionId, options) {
			return followSessionEvents(transport, sessionId, options ?? {});
		}
	};
}
//#endregion
//#region src/resources/skills.ts
/** Build the skills resource over a transport. */
function createSkillsResource(transport) {
	const path = `${API_VERSION_PREFIX}/me/skills`;
	return {
		list(options) {
			return transport.json(ListSkillsResponseSchema, {
				method: "GET",
				path,
				signal: options?.signal
			});
		},
		get(skillId, options) {
			return transport.json(GetSkillResponseSchema, {
				method: "GET",
				path: `${path}/${skillId}`,
				signal: options?.signal
			});
		},
		file(skillId, filePath, options) {
			return transport.json(SkillFileContentSchema, {
				method: "GET",
				path: `${path}/${skillId}/files/${encodeFilePath(filePath)}`,
				signal: options?.signal
			});
		},
		update(skillId, body, options) {
			return transport.json(SkillSchema, {
				method: "POST",
				path: `${path}/${skillId}`,
				body,
				signal: options?.signal
			});
		},
		delete(skillId, options) {
			return transport.noContent({
				method: "DELETE",
				path: `${path}/${skillId}`,
				signal: options?.signal
			});
		},
		previewImport(body, options) {
			return transport.json(PreviewSkillImportResponseSchema, {
				method: "POST",
				path: `${path}/import/preview`,
				body,
				signal: options?.signal
			});
		},
		import(body, options) {
			return transport.json(ImportSkillsResponseSchema, {
				method: "POST",
				path: `${path}/import`,
				body,
				signal: options?.signal
			});
		},
		previewRefresh(skillId, options) {
			return transport.json(RefreshSkillPreviewResponseSchema, {
				method: "POST",
				path: `${path}/${skillId}/refresh/preview`,
				signal: options?.signal
			});
		},
		refresh(skillId, body, options) {
			return transport.json(SkillSchema, {
				method: "POST",
				path: `${path}/${skillId}/refresh`,
				body,
				signal: options?.signal
			});
		}
	};
}
/**
* A file's path as a URL path: each segment percent-encoded, the slashes kept.
*
* The route takes the whole path in one parameter (`files/:path{.+}`), so a `reference/tone.md`
* has to arrive as two encoded segments rather than one encoded string — and a file whose name
* holds a `#`, a space or a `?` has to arrive as itself.
*/
function encodeFilePath(filePath) {
	return filePath.split("/").filter((segment) => segment.length > 0).map((segment) => encodeURIComponent(segment)).join("/");
}
//#endregion
//#region src/resources/tools.ts
/** Build the tools resource over a transport. */
function createToolsResource(transport) {
	const path = `${API_VERSION_PREFIX}/me/tools`;
	return {
		list(params, options) {
			return transport.json(ListToolSettingsResponseSchema, {
				method: "GET",
				path,
				query: {
					mode_id: params?.mode_id,
					model_id: params?.model_id
				},
				signal: options?.signal
			});
		},
		put(body, options) {
			return transport.json(ListToolSettingsResponseSchema, {
				method: "PUT",
				path,
				body,
				signal: options?.signal
			});
		}
	};
}
//#endregion
//#region src/resources/usage.ts
/** Build the usage resource over a transport. */
function createUsageResource(transport) {
	return {
		session(sessionId, options) {
			return transport.json(SessionUsageSchema, {
				method: "GET",
				path: `${sessionPath(sessionId)}/usage`,
				signal: options?.signal
			});
		},
		me(params, options) {
			return transport.json(UserUsageSchema, {
				method: "GET",
				path: `${API_VERSION_PREFIX}/me/usage`,
				query: {
					...params?.from === void 0 ? {} : { from: params.from },
					...params?.to === void 0 ? {} : { to: params.to },
					...params?.tz === void 0 ? {} : { tz: params.tz }
				},
				signal: options?.signal
			});
		}
	};
}
//#endregion
//#region src/client.ts
/**
* Create a client.
*
* @param options server URL, credentials, and the seams for tests
*/
function createClient(options) {
	const transport = createTransport({
		baseUrl: options.baseUrl,
		token: options.token,
		fetch: options.fetch,
		debug: options.onDebug
	});
	return {
		agents: createAgentsResource(transport),
		sessions: createSessionsResource(transport),
		providerCredentials: createProviderCredentialsResource(transport),
		models: createModelsResource(transport),
		modes: createModesResource(transport),
		mcpServers: createMcpServersResource(transport),
		skills: createSkillsResource(transport),
		tools: createToolsResource(transport),
		auth: createAuthResource(transport),
		preferences: createPreferencesResource(transport),
		usage: createUsageResource(transport),
		me(requestOptions) {
			return transport.json(GetMeResponseSchema, {
				method: "GET",
				path: `${API_VERSION_PREFIX}/me`,
				signal: requestOptions?.signal
			});
		},
		sendMessage(sessionId, text, messageOptions) {
			const rewind = messageOptions?.rewindTo;
			return transport.json(storedUserEventParser(EVENT_TYPES.userMessage), {
				method: "POST",
				path: sessionEventsPath(sessionId),
				body: { events: [...rewind === void 0 ? [] : [{
					type: EVENT_TYPES.sessionRewind,
					from_seq: rewind
				}], {
					type: EVENT_TYPES.userMessage,
					content: [{
						type: "text",
						text
					}],
					...messageOptions?.model === void 0 ? {} : { model: messageOptions.model },
					...messageOptions?.mode === void 0 ? {} : { mode: messageOptions.mode },
					...messageOptions?.skill === void 0 ? {} : { skill: messageOptions.skill },
					...messageOptions?.reasoningEffort === void 0 ? {} : { reasoning_effort: messageOptions.reasoningEffort }
				}] },
				signal: messageOptions?.signal
			});
		},
		interrupt(sessionId, requestOptions) {
			return transport.json(storedUserEventParser(EVENT_TYPES.userInterrupt), {
				method: "POST",
				path: sessionEventsPath(sessionId),
				body: { events: [{ type: EVENT_TYPES.userInterrupt }] },
				signal: requestOptions?.signal
			});
		}
	};
}
/**
* A schema for "the response of sending one user event, as that stored event".
*
* The wire answers with the full envelope, but {@link Client.sendMessage} and
* {@link Client.interrupt} promise the stored event itself, so the parser is composed rather
* than reused: a response that does not carry it is a validation failure — reported with the
* HTTP status the transport saw, which a second parse here could not know.
*
* @param type the event type that must come back
*/
function storedUserEventParser(type) {
	return { safeParse(value) {
		const envelope = SendEventsResponseSchema.safeParse(value);
		if (!envelope.success) return envelope;
		const stored = envelope.data.data.find((candidate) => candidate.type === type);
		if (stored === void 0) return {
			success: false,
			error: { message: `The response does not contain the stored ${type}.` }
		};
		return {
			success: true,
			data: stored
		};
	} };
}
//#endregion
//#region src/providers.ts
/**
* The presentation-only half, keyed by provider id: what a form or a tile adds to the shared
* facts. A `Record<ProviderId, …>`, so a provider that arrives in the shared list without a
* decision here is a compile error — and a provider that leaves it is one too.
*/
const PRESENTATION = {
	anthropic: { keyHint: "sk-ant-…" },
	openai: { keyHint: "sk-…" },
	google: {
		freeTier: "Free tier in Google AI Studio",
		keyHint: "AIza…"
	},
	openrouter: {
		freeTier: "Free models available",
		keyHint: "sk-or-…"
	},
	groq: {
		freeTier: "Free tier available",
		keyHint: "gsk_…"
	},
	deepseek: { keyHint: "sk-…" },
	fireworks: { keyHint: "fw_…" },
	mistral: {},
	together: {},
	xai: { keyHint: "xai-…" },
	cerebras: {}
};
/**
* The providers, in the order the shared list carries them.
*
* Built from that list rather than restated: the id, the name, the credential type and the key
* URL come from it, and {@link PRESENTATION} adds the free-tier and key-format hints. The order
* is the list's — the common providers a reader is likeliest to have first.
*/
const PROVIDERS = PROVIDERS$1.map((provider) => ({
	id: provider.id,
	name: provider.name,
	credential: provider.credential,
	keyUrl: provider.keyUrl,
	...PRESENTATION[provider.id]
}));
/** Metadata for a provider id, or `undefined` for one this list has never heard of. */
function providerInfo(id) {
	return PROVIDERS.find((provider) => provider.id === id);
}
/**
* A provider's display name, falling back to the id.
*
* The fallback is the documented behaviour rather than an error: the credential API takes any
* provider string, so a reader who typed one this list does not carry sees what they typed.
*/
function providerName(id) {
	return providerInfo(id)?.name ?? id;
}
/**
* Everything the Add-provider surfaces offer, in order: the eleven providers (the shared
* list's order) and then the named credential types.
*
* A tile is drawn per entry, and which form collects the secret is
* {@link CredentialTarget.credential} — so a new credential type with no form is a compile
* error in the frontend's form table rather than a tile that does nothing.
*/
const CREDENTIAL_TARGETS = [...PROVIDERS.map((provider) => ({
	name: provider.id,
	displayName: provider.name,
	credential: provider.credential,
	keyUrl: provider.keyUrl,
	named: false,
	modelIdHint: "model",
	...provider.freeTier === void 0 ? {} : { freeTier: provider.freeTier },
	...provider.keyHint === void 0 ? {} : { keyHint: provider.keyHint }
})), ...CREDENTIAL_TYPES.map((entry) => ({
	name: entry.defaultName,
	displayName: entry.name,
	credential: entry.type,
	named: true,
	...entry.keyUrl === void 0 ? {} : { keyUrl: entry.keyUrl },
	modelIdHint: entry.modelIdHint
}))];
/**
* What to call a **stored** credential in a sentence: the provider's display name where the
* name is a provider id, the type's display name where the name is its default, and the
* reader's own label otherwise.
*
* A second Azure credential is stored as `azure-eu`, and that is what it is called — the label
* is the reader's, and a row that hid it would leave two identical-looking rows. A **list row**
* leads with something else for a default-named credential: {@link credentialRowLabel}, which
* keeps the model-id prefix visible (#271).
*/
function credentialDisplayName(credential) {
	const known = providerInfo(credential.name);
	if (known !== void 0) return known.name;
	const entry = CREDENTIAL_TYPES.find((candidate) => candidate.type === credential.type);
	return entry !== void 0 && entry.defaultName === credential.name ? entry.name : credential.name;
}
/**
* The label a list row leads with, and the credential type's display name beside it when that
* name would hide the model-id prefix (#271).
*
* `credentialDisplayName` answers what to *call* a credential, and it is right for a sentence
* ("Saved the Azure OpenAI credential"); it is not what a row may lead with, because for a
* default-named credential it answers the type's display name — and the reader typing `/model`
* never types `Azure OpenAI`. A fixed provider is unchanged, and a reader-named credential
* already reads as itself, so two Azure credentials keep the names they were saved under.
*/
function credentialRowLabel(credential) {
	const displayName = credentialDisplayName(credential);
	if (credential.type === "api_key" || displayName === credential.name) return { primary: displayName };
	return {
		primary: credential.name,
		secondary: displayName
	};
}
/**
* The Add-provider target a **stored** credential belongs to: the provider it names, or the
* named type it is.
*
* A row's Replace opens the form on this target, and the credential's own name is what the form
* saves under — which is how a second Azure credential's row reopens *its* form rather than the
* first one's.
*/
function credentialTargetFor(credential) {
	if (providerInfo(credential.name) !== void 0) return CREDENTIAL_TARGETS.find((target) => target.name === credential.name);
	return CREDENTIAL_TARGETS.find((target) => target.named && target.credential === credential.type);
}
/**
* The non-secret facts a credential's row shows beside its name, in the order every side lists
* them (epic #245, A3c).
*
* `last4` tells two credentials of one type apart but not which service one *is*: a Bedrock key
* belongs to a region, and an account with `bedrock` and `bedrock-us` needs to see which is
* which. The facts come from the credential's `details` — the per-type object the server
* reports and never the secret — and a type with nothing to add returns none.
*
* The table is a `Record<ProviderCredentialType, …>` on purpose: a new credential type is a
* compile error here until someone decides what its row says, in one place both frontends read,
* rather than a row that silently shows nothing.
*/
const CREDENTIAL_FACT_RENDERERS = {
	api_key: () => [],
	azure_openai: () => [],
	openai_compatible: () => [],
	bedrock: (credential) => {
		const details = credential.details;
		const region = details !== void 0 && "region" in details ? bedrockRegionOf(details) : void 0;
		return region === void 0 ? [] : [region];
	},
	vertex: (credential) => {
		const details = credential.details;
		if (details === void 0 || !("email" in details)) return [];
		return [
			details.email,
			details.project,
			details.location
		];
	}
};
/** The facts one stored credential's row shows beside its name and `last4`. */
function credentialFacts(credential) {
	return CREDENTIAL_FACT_RENDERERS[credential.type](credential);
}
//#endregion
//#region src/tasks.ts
/**
* The parent-side tools that are a task's machinery rather than a tool line (epic #340, S5).
*
* A card draws where the `agent` call is, an `agent_message` draws as a line of its own
* inside it, and `agent_wait` / `agent_stop` are the reader's own affordances rather than
* something the model did worth a line — so none of the four is drawn as an ordinary tool call
* (S9). The names are the protocol's own (#344 spells them once for the deployment that offers
* them, the brain that answers them and the frontends that draw the card); `message_parent` is
* deliberately **not** one of them, because it is a **child's** tool and never appears in the
* parent's log.
*
* It is **not exported** (#367's nit, #368): both frontends ask the predicate below and nothing
* else ever wanted the list, and an unused export is what drifts silently at the next wire
* change.
*/
const TASK_TOOL_NAMES = [
	AGENT_TOOL_NAME,
	AGENT_MESSAGE_TOOL_NAME,
	TASK_WAIT_TOOL_NAME,
	AGENT_STOP_TOOL_NAME
];
/**
* Whether a tool call is one of the task machinery's own (epic #340, S9).
*
* The test the transcript suppresses a tool line with, and the one a frontend uses when it
* decides which calls a card stands in for. It is the four names and nothing else: a remote
* MCP server's tool is offered as `<server>__<tool>`, so no server can borrow the `agent`
* prefix.
*
* @param name the name the model called the tool by
*/
function isTaskToolName(name) {
	return TASK_TOOL_NAMES.includes(name);
}
/**
* Whether a task is still working — the one state a card follows its child's stream for, and the
* one it offers **Stop** in (epic #340, S9; #368).
*
* A finished task's activity line would be a line about nothing (the child is not doing
* anything), and watching it would hold an SSE connection open — with the server's keepalive —
* for as long as a screen holds the card: `watchTaskActivity`'s reference count exists for
* exactly this, and a card that is done has nothing to watch. Both frontends draw the same line
* with it, which is why it lives here rather than in each of them: the web app's card passed it
* to its watcher while `oh`'s card opened one for every card whatever its status, and one
* predicate is what keeps the two from disagreeing about where that line is.
*
* `waiting` is live: a child parked in its own `agent_wait` is still working, and the status
* only `running` can be refined to it comes from the very stream being watched.
*
* @param status the status a card draws, after {@link taskCardStatus} refined it
*/
function taskIsLive(status) {
	return status === "running" || status === "waiting";
}
/**
* The status a card draws, given what the child's own stream says (epic #340, S9).
*
* The refinement agreed with the maintainer: the parent's `task_status` stays
* `running | idle | failed | stopped`, and **waiting** is the child's own fact — its last
* `session.status_idle` ended with `waiting_on_tasks` — so a frontend that is watching the
* child passes it in here. Only a task the parent still calls `running` can be waiting;
* anything else — a task that finished, failed or was stopped — has an answer waiting cannot
* improve on.
*
* @param status the status the parent's log derived ({@link TaskCard.status})
* @param waiting whether the child's own log says it is paused in an `agent_wait`
*/
function taskCardStatus(status, waiting) {
	return waiting && status === "running" ? "waiting" : status;
}
/**
* The tasks a session's log holds, in the order a card draws (epic #340, S4; issue #345).
*
* One pass over the events, and the whole card comes out of it:
*
* - `session.task_started` opens a card and places it at the `agent` call that started it —
*   the call whose id its `tool_use_id` names, or, for a start without one, the nearest
*   unmatched `agent` call carrying its description;
* - `session.task_status` moves the status, the newest one winning;
* - `session.task_notification` adds a line (`message`) or the latest outcome (`result` /
*   `failed`);
* - an `agent_message` call adds the parent's own half of the conversation.
*
* The status is the parent's four words mapped onto the card's five: `stopped` and `failed`
* are themselves, `idle` is **done** once a result has been delivered (S9) — and, before one
* has been, still `running`, because a turn that ended without telling the parent anything is
* not a completion — and `running` is `running`.
*
* A log that holds an `agent.tool_result` or an unrelated call simply has nothing to say here:
* the derivation reads the task events and the `agent*` calls, and ignores everything else, so
* it can be handed a whole session log or just the events a transcript kept.
*
* @param events the events to fold, in log order — a whole session's log, or a transcript's
*   task events
*/
function tasksFromEvents(events) {
	const builders = /* @__PURE__ */ new Map();
	const calls = [];
	for (const event of events) {
		if (event.type === EVENT_TYPES.agentToolUse) {
			const name = toolCallOfferedName(event);
			const input = toolCallInput(event);
			if (name === AGENT_TOOL_NAME) calls.push({
				id: event.id,
				seq: event.seq,
				description: stringField(input, "description")
			});
			else if (name === AGENT_MESSAGE_TOOL_NAME) {
				const taskId = stringField(input, "task_id");
				const text = stringField(input, "text");
				const builder = taskId === null ? void 0 : builders.get(taskId);
				if (builder !== void 0 && text !== null) builder.messages.push({
					from: "parent",
					text,
					seq: event.seq
				});
			}
			continue;
		}
		if (event.type === EVENT_TYPES.sessionTaskStarted) {
			const builder = {
				id: event.task_id,
				kind: event.kind,
				childSessionId: event.child_session_id ?? null,
				type: event.task_type ?? null,
				description: event.description,
				parentStatus: "running",
				messages: [],
				result: null,
				position: claimCall(calls, event.tool_use_id, event.description) ?? event.seq
			};
			builders.set(event.task_id, builder);
			continue;
		}
		if (event.type === EVENT_TYPES.sessionTaskStatus) {
			const builder = builders.get(event.task_id);
			if (builder !== void 0) builder.parentStatus = event.status;
			continue;
		}
		if (event.type === EVENT_TYPES.sessionTaskNotification) {
			const builder = builders.get(event.task_id);
			if (builder === void 0) continue;
			if (event.kind === "message") builder.messages.push({
				from: "child",
				text: event.text,
				seq: event.seq
			});
			else builder.result = {
				kind: event.kind,
				text: event.text,
				seq: event.seq
			};
		}
	}
	return [...builders.values()].map((builder) => ({
		id: builder.id,
		kind: builder.kind,
		childSessionId: builder.childSessionId,
		type: builder.type,
		description: builder.description,
		status: cardStatus(builder.parentStatus, builder.result !== null),
		messages: builder.messages,
		result: builder.result,
		position: builder.position
	})).sort((left, right) => left.position - right.position);
}
/** The card's status from the parent's four and whether a turn's outcome has landed. */
function cardStatus(status, hasResult) {
	if (status === "idle") return hasResult ? "done" : "running";
	return status;
}
/**
* The `agent` call that started a task, or `null` when the log no longer holds it.
*
* A start that names its call (`tool_use_id`) is answered by that call alone — the exact join,
* and why the field was added (#345): two calls may share a description, and the older
* heuristic then put a card at the wrong one. The id is matched against the calls this fold has
* seen, and anything it cannot find (a trimmed replay) is `null`, so the card falls back to the
* start's own `seq` rather than being placed at some other call.
*
* A start **without** the field — every log stored before it existed — keeps the old heuristic:
* the nearest unmatched call whose description is the task's own, which is what the call and
* the start share there. A matched call is removed so two tasks cannot claim one call, and a
* call that matches nothing leaves the others alone.
*
* @param calls the unmatched `agent` calls, oldest first
* @param toolUseId the id of the call that started the task, when the start names one
* @param description the task's own description — the fallback's only join
*/
function claimCall(calls, toolUseId, description) {
	if (toolUseId !== void 0) {
		const index = calls.findIndex((call) => call.id === toolUseId);
		if (index === -1) return null;
		const [call] = calls.splice(index, 1);
		return call?.seq ?? null;
	}
	for (let index = calls.length - 1; index >= 0; index -= 1) if (calls[index]?.description === description) {
		const [call] = calls.splice(index, 1);
		return call?.seq ?? null;
	}
	return null;
}
/** A string field of a tool call's input, or `null` when it is absent or not a string. */
function stringField(input, key) {
	const value = input[key];
	return typeof value === "string" && value.length > 0 ? value : null;
}
//#endregion
//#region src/tools.ts
/** The words a call's status is drawn with (epic #303, X5; issue #308). */
const STATUS_WORDS = {
	running: "running",
	waiting: "waiting for you",
	done: "done",
	error: "failed",
	denied: "denied",
	dismissed: "dismissed",
	interrupted: "interrupted",
	lost: "execution lost"
};
/**
* The words for a status, the same in both frontends — "waiting for you" is a sentence a reader
* is owed, not chrome either side may reword.
*
* @param status the call's state
*/
function toolStatusLabel(status) {
	return STATUS_WORDS[status];
}
/**
* The status of a call, from what the log says about it (epic #303, X1/X6; issue #308).
*
* The three inputs are the whole of it: what the policy evaluated the call under, whether a
* result has landed (and what it said), and the turn's own state. A call with no result is
* `waiting` when it is one the reader has to answer — its permission is `ask`, or the turn ended
* naming it `requires_action` — and `running` while the turn is still working; a call with no
* result on a turn that has ended is `lost`, the one thing the log can say about a call nothing
* answered.
*
* @param permission the call's `evaluated_permission`
* @param result what answered the call, or `undefined` while nothing has
* @param options `waiting` when the turn ended on this call, `running` while the turn is working
*/
function toolCallStatus(permission, result, options) {
	if (result !== void 0) return statusFromResult(result);
	if (permission === "ask" || options.waiting) return "waiting";
	return options.running ? "running" : "lost";
}
/**
* The status a result's own words mean — the brain's sentences are the record (epic #303, X3/X6).
*
* A result is `is_error` for everything that is not what the tool produced, so the text is what
* tells a denial from an interrupt from a lost execution from a tool's own failure. The phrases
* are the brain's own (`packages/brain/src/pausing.ts`, `tools.ts`), matched the way they are
* written; anything else `is_error` is the tool's failure.
*/
function statusFromResult(result) {
	if (!result.isError) return "done";
	const text = result.content;
	if (text.startsWith("Interrupted by the user.")) return "interrupted";
	if (text.startsWith("The user sent a message instead.")) return "dismissed";
	if (text.includes("execution lost")) return "lost";
	if (/^Permission to use .+ has been denied\./.test(text) || text.startsWith("The user denied this")) return "denied";
	return "error";
}
/**
* The built-in tool names this module knows how to summarize.
*
* Spelled here rather than imported: `web_fetch` and `web_search` are named by
* `@openharness/hands`, which this package may not depend on (the allowed graph is
* `protocol` alone), and their names are part of the wire the log records.
*/
const WEB_FETCH_TOOL = "web_fetch";
const WEB_SEARCH_TOOL = "web_search";
/**
* The two tools a skill is read with (epic #349, K6; #352; the line is #353).
*
* Spelled here for the same reason the two above are: `@openharness/hands` owns them and this
* package may not depend on it, while the names are part of the wire the log records.
*/
const SKILL_TOOL = "skill";
const SKILL_READ_TOOL = "skill_read";
/** The input fields a short summary of a call is drawn from, keyed by the built-in tools. */
const SUMMARY_FIELDS = {
	[WEB_FETCH_TOOL]: ["url"],
	[WEB_SEARCH_TOOL]: ["query"],
	[SKILL_TOOL]: ["name"],
	[SKILL_READ_TOOL]: ["path"]
};
/**
* A short summary of what a call asked for, or `null` when the input says nothing short
* (epic #303, X5; issue #308) — the one-line part of a call's compact row.
*
* The per-tool cases are the ones worth their own words: a fetch is its URL, a search its query,
* `todo_write` its task count and `ask_user` its first question. Everything else falls back to
* the first string value in the input, so an `echo` reads as its text and an unknown tool (an
* MCP one, say) shows what it can rather than nothing.
*
* @param call the call's name and input
*/
function toolCallSummary(call) {
	for (const field of SUMMARY_FIELDS[call.name] ?? []) {
		const value = call.input[field];
		if (typeof value === "string" && value.length > 0) return value;
	}
	if (call.name === TODO_WRITE_TOOL_NAME) {
		const todos = call.input.todos;
		if (Array.isArray(todos)) return todos.length === 1 ? "1 task" : `${todos.length} tasks`;
	}
	if (call.name === ASK_USER_TOOL_NAME) {
		const questions = call.input.questions;
		if (Array.isArray(questions)) {
			const first = questions[0];
			if (isJsonObject(first) && typeof first.question === "string" && first.question.length > 0) return first.question;
			return questions.length === 1 ? "1 question" : `${questions.length} questions`;
		}
	}
	return firstString(call.input);
}
/**
* A skill call's whole compact line, or `null` for every other tool (epic #349, K6; #352).
*
* A `skill` call reads one of the reader's imported skills and a `skill_read` call reads one
* file of it, and `skill release-notes` is not what either of those is: the tool's own name says
* nothing a reader wants, and the two calls differ by which of them was made. So this answers
* the sentence instead — `Loaded skill release-notes`, `Read reference.md` — for a caller that
* draws the call as one line, which is what makes the two calls tell the reader what happened
* rather than which tool ran. The built-in tool's own name is the fallback when the input does
* not carry what the sentence needs.
*
* It is `null` for every other call, so a caller draws its compact line its own way and only
* these two are special — the same one-place rule `toolCallSummary` follows for the per-tool
* words, and the same one `oh` reads, so both frontends say it identically.
*
* @param call the call's name and input
*/
function skillToolCallLabel(call) {
	if (call.name === SKILL_TOOL) {
		const name = call.input.name;
		return typeof name === "string" && name.length > 0 ? `Loaded skill ${name}` : "Loaded skill";
	}
	if (call.name === SKILL_READ_TOOL) {
		const path = call.input.path;
		return typeof path === "string" && path.length > 0 ? `Read ${path}` : "Read a file";
	}
	return null;
}
/** The first string value of an object, in insertion order, or `null`. */
function firstString(input) {
	for (const value of Object.values(input)) if (typeof value === "string" && value.length > 0) return value;
	return null;
}
/** Whether a JSON value is an object (and not an array or `null`). */
function isJsonObject(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
* The input rendered for a reader who expanded the line, pretty-printed and stable.
*
* The JSON a model produced, two-space indented: the full input is the one thing the compact line
* exists to hide, so this is what it shows instead when opened.
*/
function formatToolInput(input) {
	return JSON.stringify(input, null, 2);
}
/**
* Where a remote tool came from, as the marker a call line and an approval prompt both draw
* (epic #303, X6; #308; #312).
*
* One spelling for one fact — `mcp: notes`, and plain `mcp` for a call the log does not say the
* server of — so the page and the terminal cannot answer "whose tool is this?" two ways. It is
* the parenthesized half, not the parentheses: the terminal writes `(mcp: notes)`, the web app
* keeps the badge's own words and puts this in its `title`. `call.server` is what the log
* records on an `agent.mcp_tool_use`, so a reader of the log alone arrives at the same marker.
*
* @param server the MCP server's name, or `undefined` when the log names none
*/
function mcpServerMarker(server) {
	return server === void 0 ? "mcp" : `mcp: ${server}`;
}
/**
* How many searches a chat's calls add up to (epic #303, X5; #305; #335; issue #308).
*
* A provider-executed search is stored as a `web_search` call like any other (X14) — beside the
* provider's own content in `agent.opaque_content` — so a screen that is already following a
* chat counts it here without asking the usage route. The routes answer the same number the
* other way round, from each request's span, and count the operator-key era's calls as well.
*
* It is a count and not a price here, because a screen that has the calls has no provider to
* attribute a fee to — the usage routes price the ones that carry a published rate
* (`@openharness/protocol`'s `SEARCH_PRICE_PER_CALL`) — which is why it is drawn beside the cost
* rather than as a term in it. A call counts whether it succeeded or not: a search the provider
* reported as failed is still a search the model made.
*/
function searchCount(calls) {
	return calls.filter((call) => call.name === WEB_SEARCH_TOOL).length;
}
/** The truncation record a `span.model_request_start` carries, as the transcript keeps it. */
function truncatedResultsFrom(truncation) {
	return (truncation.results ?? []).map((result) => ({
		seq: result.seq,
		tool: result.tool,
		tokensBefore: result.tokens_before,
		tokensAfter: result.tokens_after
	}));
}
/** The clearing record a `span.model_request_start` carries, as the transcript keeps it. */
function clearedResultsFrom(cleared) {
	return {
		results: cleared.results,
		tokens: cleared.tokens
	};
}
/**
* The line a reader is owed when a request had to shorten tool results, or `null` when it did not.
*
* Counts rather than names, because a request may cap several results of one step and the count
* is what the reader acts on; the whole answer is still in the transcript.
*/
function truncatedResultsNotice(results) {
	if (results.length === 0) return null;
	return results.length === 1 ? "One tool result was too long for this model and was shortened." : `${results.length} tool results were too long for this model and were shortened.`;
}
/**
* The line a reader is owed when a request cleared old tool results, or `null` when it did not.
*
* Clearing is the first answer to a filling context (X9) — no model call, nothing summarized —
* so it is a fact worth saying rather than a silent edit to what the model was shown.
*/
function clearedResultsNotice(cleared) {
	if (cleared === null || cleared.results === 0) return null;
	return cleared.results === 1 ? "An older tool result was cleared to make room in the context." : `${cleared.results} older tool results were cleared to make room in the context.`;
}
/** The sentence a turn that ran out of tool steps is reported with (epic #303, X2; #308). */
const TOOL_STEPS_EXHAUSTED_NOTICE = "This turn reached its tool-step limit before the model finished, so it was ended.";
/**
* The step-limit notice from the log's last error, or `null` (epic #303, X2; issue #308).
*
* The brain sends its own sentence in the `session.error` (`tool_steps_exhausted_error`) — how
* many steps it had, and that a new message carries on — so that message is what a reader is
* shown; {@link TOOL_STEPS_EXHAUSTED_NOTICE} is only the fallback for one that carried none.
*
* @param error the transcript's `lastError`
*/
function stepLimitNotice(error) {
	if (error === null || error.type !== "tool_steps_exhausted_error") return null;
	return error.message.length > 0 ? error.message : TOOL_STEPS_EXHAUSTED_NOTICE;
}
/** The sentence a turn the model looped on is reported with (epic #303, #331). */
const TOOL_LOOP_STUCK_NOTICE = "This turn repeated the same failing tool call too many times, so it was ended.";
/**
* The stuck-loop notice from the log's last error, or `null` (epic #303, issue #331).
*
* The brain sends its own sentence in the `session.error` (`tool_loop_stuck_error`) — what the
* model kept calling and that a new message carries on — so that message is what a reader is
* shown; {@link TOOL_LOOP_STUCK_NOTICE} is only the fallback for one that carried none. It is
* the same shape as {@link stepLimitNotice}: a turn that ended on a loop says so rather than
* being drawn as a red error.
*
* @param error the transcript's `lastError`
*/
function stuckNotice(error) {
	if (error === null || error.type !== "tool_loop_stuck_error") return null;
	return error.message.length > 0 ? error.message : TOOL_LOOP_STUCK_NOTICE;
}
/** The sentence shown when the chat's model cannot call tools at all (epic #303, X2; #308). */
const TOOLS_UNSUPPORTED_NOTICE = "This model can't use tools.";
/**
* Whether a catalog entry's model can call tools, or `null` when the catalog does not say
* (epic #303, X2; issue #308).
*
* `tool_call` is the server's answer, read off models.dev, and `true` for a model the registry
* does not know. A caller with no entry — the catalog has not loaded, or the chat runs a
* free-text id it does not list — gets `null`: nothing is claimed either way, so a screen shows
* no notice rather than a wrong one.
*
* @param entry the catalog entry for the model the chat runs
*/
function modelSupportsTools(entry) {
	return entry === void 0 || entry === null ? null : entry.tool_call;
}
//#endregion
//#region src/transcript.ts
/**
* Whether a `session.error` type is one an MCP server's failure is reported with (epic #303,
* X10; #313).
*/
function isMcpFailure(type) {
	return type === "mcp_connection_failed_error" || type === "mcp_authentication_failed_error";
}
/**
* The message a request's truncation record is about, or `null` (epic #277, K6; #314).
*
* The record's `seq`/`tokens_before`/`tokens_after` describe *the newest item* the request had
* to shorten, and since #306 that item may be a **tool result** rather than a message: a request
* that capped only results names the newest of them, and `results` lists every one of them
* (`packages/brain`'s `truncationRecord` — the protocol's `TruncationSchema` documents it). Read
* as a message's cut — which is what a frontend drawing "your message was too long" from this
* field did — one cut produced two notices that contradict each other: the results notice saying
* a tool's answer was shortened, and the truncation notice blaming the reader's message for it
* (#314).
*
* The record's two halves are what tells them apart, so that is the whole rule: a `seq` the
* request's own `results` list carries is a **tool result's** cut and belongs to that notice
* alone, and anything else is the newest message's — which is the only other thing `capNewest`
* caps. No wire change is needed for this, and none is made: the record already said both.
*/
function messageTruncation(truncated, recordedAt) {
	if ((truncated.results ?? []).some((result) => result.seq === truncated.seq)) return null;
	return {
		seq: truncated.seq,
		tokensBefore: truncated.tokens_before,
		tokensAfter: truncated.tokens_after,
		recordedAt
	};
}
/**
* The state for a session with no events yet.
*
* `lastSeq` is `0`, the protocol's "from the start": passing it as `afterSeq` replays the
* whole log. `deleted` is `false`: nothing has happened yet. `model` is `null` unless a
* {@link TranscriptSeed} names one — which is how a frontend that opened a session tells the
* transcript which model its first message is a continuation of (#268).
*
* @param seed the session's model, when the caller knows it; see {@link TranscriptSeed}
*/
function initialTranscriptState(seed = {}) {
	return {
		messages: [],
		status: "idle",
		lastError: null,
		mcpFailure: null,
		lastSeq: 0,
		deleted: false,
		model: seed.model ?? null,
		pendingRequests: [],
		usage: null,
		summaries: [],
		summarizing: null,
		context: null,
		truncation: null,
		manualCompaction: null,
		toolCalls: [],
		truncatedToolResults: [],
		confirmations: [],
		clearedToolResults: null,
		toolSources: {},
		todos: null,
		todoEvents: [],
		tasks: [],
		taskEvents: []
	};
}
/**
* Fold one event into the transcript.
*
* Pure: the state that comes back is a new object, and the one that went in is untouched — an
* incoming event is only ever read, never written — so a framework can compare states by
* reference and a caller can hand over a shared, frozen event.
*
* Events are idempotent — an event at or below `state.lastSeq` is dropped — which is what lets
* history and a resumed stream overlap without doubling a message. Since D9 the streamed chunks
* of a reply are stored events too (`event_start` / `event_delta` with a `seq`), so they take
* the same path: they are deduplicated by `seq`, they advance `lastSeq`, and a client that
* resumes mid-reply gets the rest of the chunks rather than skipping them. The pre-D9
* stream-only previews — a chunk with no envelope — were removed in phase P4: apart from the
* stream-only `session.deleted` (handled below), every event this reducer takes is a
* `StoredEvent`.
*
* The rules, in one place:
*
* - **A delta appends to its message's preview**, creating the preview if the client missed
*   (`event_delta` carries the id of the message being previewed). A delta for a message that
*   is already stored is ignored: the stored event is the record.
* - **A stored `agent.message` replaces whatever the transcript holds for its id** — a whole
*   preview, a partial one, or nothing at all. It never merges.
* - **A stored preview sits where its chunks started**: at its `event_start`'s `seq`, or at
*   its first delta's `seq` when the start was skipped (a client that joined mid-reply).
* - **A finished reply sits where it started.** An `agent.message` that carries `supersedes`
*   sorts at `from_seq`, whether or not the client saw the chunks, so a steer sent mid-reply
*   stays behind the reply in every view. A reply with no range — a log stored before D9 —
*   keeps the position of the preview it replaces, or, with no preview to replace, its own
*   `seq`.
* - **Unfinished previews are dropped when the turn moves on**: `span.model_request_end` and
*   `session.status_idle` discard previews still streaming (#40), which since D9 includes the
*   previews of a chunk range a span end supersedes. An interrupt is not this case: its
*   partial reply is stored as an `agent.message` before the span closes.
* - **`pending` clears on the claim.** The event that answers a user message names it in its
*   `consumes`: a `span.model_request_start` claims the messages its request folds in (P3),
*   and a `span.model_request_end` or `session.status_idle` claims the interrupts it ends on
*   (P4). A claim event with no list at all — a log stored before D9 — keeps the old reading:
*   a span start says everything pending was picked up, and a span end or an idle says
*   nothing.
* - **`session.deleted` is terminal, seq-less and idempotent.** It has no `seq` (it is
*   stream-only), so it is folded in *before* the dedupe below — a replayed log has no
*   position for it — and the only thing it does is set `deleted` to `true`. The stream ends
*   after it, so a UI can react to the state rather than to the end of an iteration, and a
*   second one changes nothing.
* - **A `session.rewind` drops the conversation it replaced** (#238). Editing a message
*   restarts the session from it: the rewind carries the range it replaces — from the edited
*   message through the last event before it — and everything a client is showing from there
*   on belongs to a branch the session is no longer on. A client that followed the log live
*   has all of it on screen (a reload would never have shown it, since replay skips the
*   range), so the rewind drops those messages, the error it replaced, and the requests of
*   the turn it replaced. The test is each message's {@link TranscriptMessage.position}: a
*   user message sits at its own `seq` and a reply at the `from_seq` of the chunks it
*   replaced, so everything at or after the range's `from_seq` is inside it.
* - **A `user.message` carrying a `model` may switch the session's model.** When its id
*   differs from `state.model`, the message carries `modelChangedTo` so a UI can draw the
*   marker, and `state.model` becomes the new id. `state.model` starts at the seed a frontend
*   gave it — the session's own model — so a chat started from a model marks its first switch
*   (#268), and a message naming the model already in effect changes nothing. A message with
*   no `model` leaves `state.model` alone. The model a `span.model_request_start` names is
*   taken as `state.model` too: the log says which model each request really ran, which is
*   what keeps a resumed chat's markers identical to the live view's.
* - **A reply carries what it cost** (epic #201, U1). Its {@link TranscriptMessage.meta}
*   comes from the turn's spans: a `span.model_request_start` names the model and opens a
*   tracked request, its `span.model_request_end` reports the tokens and closes it, and the
*   reply takes them all. Since the span end follows the reply, the metadata is written
*   twice — the model when the reply lands, the tokens and the duration when the end arrives
*   — and a reply the brain retried takes the request that failed too, so its tokens add up.
*   Nothing the log does not say is invented: an absent field is `undefined`, never `0`, and
*   a turn that ends (`session.status_idle`) drops the requests no reply claimed.
* - **A summary is a mark, not a cut** (epic #277, K1/K10; #280). A `session.context_summary`
*   supersedes nothing: the messages stay exactly as they were, the divider is added at the last
*   `seq` the summary covers, and only a `session.rewind` takes one back — the same rule the
*   messages it replaced get. A `session.context_summary_progress` sets the summary-in-flight the
*   transcript shows as "Summarizing… N of M", and the summary landing, the chat's own request
*   starting, a `session.error` and a turn ending all clear it, so a compaction is never
*   reported after it is over. The summary also moves the **context meter**: it replaces the
*   history it covers, so from there until the next real request reports its own prompt size the
*   meter is an estimate ({@link TranscriptContext.estimated}). A request the compaction engine
*   made (`purpose: 'summary'`) measures the summarizer's prompt, so it names no reply's model,
*   adds no tokens to one, and never becomes the meter's baseline.
* - **A request that had to shorten its newest item says so.** Its
*   `span.model_request_start.truncated` becomes {@link TranscriptState.truncation} — "your
*   message was too long for this model and was shortened" — and the newest real request
*   replaces it, so a request that capped nothing clears the notice rather than leaving it on
*   screen for the life of the session.
* - **A background task is a card, not a tool call** (epic #340, S9; #345). The three task
*   events are kept and {@link TranscriptState.tasks} is recomputed from them, exactly as the
*   todo list is; the task machinery's own `agent*` calls are kept with them and **not** drawn
*   as tool lines, so the card takes the `agent` call's place.
*
* @param state the transcript so far
* @param event the next event, from `iterate`, `stream`, or anywhere else
*/
function reduceTranscript(state, event) {
	if (event.type === EVENT_TYPES.sessionDeleted) return state.deleted ? state : {
		...state,
		deleted: true
	};
	if (event.type === EVENT_TYPES.sessionRewind) {
		const reduced = reduceStoredEvent(state, event);
		if (reduced === state && event.seq <= state.lastSeq) return state;
		return {
			...reduced,
			lastSeq: Math.max(state.lastSeq, event.seq)
		};
	}
	if (event.seq <= state.lastSeq) return state;
	return {
		...reduceStoredEvent(state, event),
		lastSeq: event.seq
	};
}
/**
* Fold a whole sequence into the transcript: history, or every event of a stream.
*
* @param state the transcript so far
* @param events the events, in order
*/
function reduceTranscriptAll(state, events) {
	let next = state;
	for (const event of events) next = reduceTranscript(next, event);
	return next;
}
/** The stored-event half of {@link reduceTranscript}. */
function reduceStoredEvent(state, event) {
	switch (event.type) {
		case EVENT_TYPES.userMessage: return fromUserMessage(state, event);
		case EVENT_TYPES.agentMessage: return fromAgentMessage(state, event);
		case EVENT_TYPES.userInterrupt: return state;
		case EVENT_TYPES.agentToolUse:
		case EVENT_TYPES.agentMcpToolUse: return fromToolUse(state, event);
		case EVENT_TYPES.agentToolResult:
		case EVENT_TYPES.agentMcpToolResult: return fromToolResult(state, event);
		case EVENT_TYPES.userToolConfirmation: return recordConfirmation(state, event);
		case EVENT_TYPES.eventStart: return startPreview(state, event.event.id, event.seq);
		case EVENT_TYPES.eventDelta: return appendDelta(state, event.event_id, event.delta.index, event.delta.content.text, event.seq);
		case EVENT_TYPES.sessionStatusRunning:
		case EVENT_TYPES.sessionStatusRescheduled: return {
			...state,
			status: "running",
			toolCalls: refreshToolStatuses(state, "running")
		};
		case EVENT_TYPES.sessionStatusIdle: return clearClaimedPending({
			...state,
			status: "idle",
			messages: withoutPreviews(state.messages),
			pendingRequests: [],
			summarizing: null,
			toolCalls: refreshToolStatuses(state, "idle")
		}, event.consumes);
		case EVENT_TYPES.sessionUsage: return {
			...state,
			usage: usageFromEvent(event)
		};
		case EVENT_TYPES.sessionRewind: return dropRewound(state, event);
		case EVENT_TYPES.sessionError: {
			const error = {
				type: event.error.type,
				message: event.error.message,
				retryStatus: event.error.retry_status.type
			};
			return {
				...state,
				lastError: error,
				mcpFailure: isMcpFailure(error.type) ? {
					...error,
					seq: event.seq
				} : state.mcpFailure,
				summarizing: null
			};
		}
		case EVENT_TYPES.sessionCompact: return {
			...state,
			manualCompaction: {
				pending: true,
				outcome: null,
				seq: event.seq
			}
		};
		case EVENT_TYPES.sessionCompaction: return {
			...state,
			manualCompaction: {
				pending: false,
				outcome: event.outcome,
				...event.message === void 0 ? {} : { message: event.message },
				seq: event.seq
			}
		};
		case EVENT_TYPES.sessionContextSummary: return fromContextSummary(state, event);
		case EVENT_TYPES.sessionContextSummaryProgress: return {
			...state,
			summarizing: {
				pass: event.pass,
				passes: event.passes,
				seq: event.seq
			}
		};
		case EVENT_TYPES.sessionTaskStarted:
		case EVENT_TYPES.sessionTaskStatus:
		case EVENT_TYPES.sessionTaskNotification: return withTaskEvents(state, event);
		case EVENT_TYPES.modelRequestStart:
			if (event.purpose === "summary") return {
				...state,
				pendingRequests: [...state.pendingRequests, {
					...openedRequest(event),
					summary: true
				}]
			};
			return clearClaimedPending({
				...state,
				...event.model === void 0 ? {} : { model: event.model },
				pendingRequests: [...state.pendingRequests, openedRequest(event)],
				summarizing: null,
				truncation: event.truncated === void 0 ? null : messageTruncation(event.truncated, event.seq),
				toolSources: event.tools === void 0 ? state.toolSources : sourcesFrom(event.tools),
				truncatedToolResults: event.truncated === void 0 ? [] : truncatedResultsFrom(event.truncated),
				clearedToolResults: event.cleared === void 0 ? null : clearedResultsFrom(event.cleared)
			}, event.consumes, { absentMeansAll: true });
		case EVENT_TYPES.modelRequestEnd: return closeRequest(clearClaimedPending({
			...state,
			messages: withoutPreviews(state.messages)
		}, event.consumes), event);
		default: return state;
	}
}
/**
* Drop the conversation a `session.rewind` replaced (#238).
*
* The message at the range's `from_seq`, its reply, and everything after them are what the
* reader took back: they are no longer part of what the session is. A client that watched the
* log live is showing them, so this is where the two views agree — a client that loads the
* session later never receives them at all, because replay skips the range.
*
* The turn's requests go with them, for the reason `session.status_idle` drops them: a
* request no reply ever claimed would otherwise fold its tokens into the next turn's reply.
* The error goes too: whatever it was about was inside the range.
*
* `position` is the test — a message's own `seq` for a user message, the `from_seq` of the
* chunks it replaced for a reply — so a message the reader saw before the edit stays exactly
* where it was.
*/
function dropRewound(state, event) {
	const { from_seq, to_seq } = event.supersedes;
	const inRange = (seq) => seq >= from_seq && seq <= to_seq;
	const messages = state.messages.filter((message) => message.position < from_seq || message.position > to_seq);
	const summaries = state.summaries.filter((summary) => !inRange(summary.seq));
	const progress = state.summarizing;
	const summarizing = progress !== null && inRange(progress.seq) ? null : progress;
	const notice = state.truncation;
	const truncation = notice !== null && inRange(notice.recordedAt) ? null : notice;
	const compaction = state.manualCompaction;
	const manualCompaction = compaction !== null && inRange(compaction.seq) ? null : compaction;
	const toolCalls = state.toolCalls.filter((call) => !inRange(call.position));
	const todoEvents = state.todoEvents.filter((event) => !inRange(event.seq));
	const taskEvents = state.taskEvents.filter((event) => !inRange(event.seq));
	const confirmations = state.confirmations.filter((confirmation) => !inRange(confirmation.seq));
	if (messages.length === state.messages.length && summaries.length === state.summaries.length && summarizing === state.summarizing && truncation === state.truncation && manualCompaction === state.manualCompaction && toolCalls.length === state.toolCalls.length && todoEvents.length === state.todoEvents.length && taskEvents.length === state.taskEvents.length && confirmations.length === state.confirmations.length && state.truncatedToolResults.length === 0 && state.clearedToolResults === null && state.lastError === null && state.mcpFailure === null && state.pendingRequests.length === 0 && state.usage === null && state.context === null) return state;
	return {
		...state,
		messages,
		summaries,
		summarizing,
		truncation,
		manualCompaction,
		toolCalls,
		truncatedToolResults: [],
		clearedToolResults: null,
		todoEvents,
		taskEvents,
		confirmations,
		todos: readTodoList(todoEvents),
		tasks: tasksFromEvents(taskEvents),
		lastError: null,
		mcpFailure: state.mcpFailure !== null && inRange(state.mcpFailure.seq) ? null : state.mcpFailure,
		pendingRequests: [],
		usage: null,
		context: null
	};
}
/**
* Clear the `pending` flag of the messages a claim reaches.
*
* The claim is the event's `consumes` list — a `span.model_request_start` naming the messages
* its request folds in, or a `span.model_request_end` / `session.status_idle` naming the
* interrupts it ended on (P4). Only what the list names is cleared, and a message that is
* already delivered is left alone, so the state keeps its identity when nothing changes.
*
* `absentMeansAll` is the pre-D9 fallback, for a log whose writer claimed out of band: a span
* start with no list says everything pending when a request starts has just been picked up,
* which is how every message was read before the claims existed. For the other two event
* types an absent list claims nothing — their older form never claimed anything.
*
* @param state the transcript so far
* @param consumes the ids the event claims, or `undefined` when it carries no list
* @param options.absentMeansAll what a missing list means; `false` by default
*/
function clearClaimedPending(state, consumes, options = {}) {
	if (consumes === void 0 && options.absentMeansAll !== true) return state;
	const claimed = consumes === void 0 ? null : new Set(consumes);
	let changed = false;
	const messages = state.messages.map((message) => {
		if (!message.pending || claimed !== null && !claimed.has(message.id)) return message;
		changed = true;
		return {
			...message,
			pending: false
		};
	});
	return changed ? {
		...state,
		messages
	} : state;
}
/**
* The messages that outlive a reply that is not coming: everything that is not still a preview.
*
* A preview the log never reconciled has no text anyone stored, so there is nothing to keep —
* what it stood in for did not happen.
*/
function withoutPreviews(messages) {
	return messages.filter((message) => !message.streaming);
}
/** The name → source map a request's offered-tools record becomes (epic #303, X1; #308). */
function sourcesFrom(tools) {
	const sources = {};
	for (const tool of tools) sources[tool.name] = tool.source;
	return sources;
}
/**
* Fold a tool call in: one call line, at the event's own `seq` (epic #303, X1; #308; #312).
*
* One fold for both pairs — a built-in's `agent.tool_use` and a remote MCP server's
* `agent.mcp_tool_use` — read through the protocol's `events/tool.ts` helpers, so a reader
* cannot handle one pair and quietly drop the other. What they differ in is read there: the
* call's id (`toolCallId`), where its tool comes from (`toolCallSource`) and the server it
* belongs to (`toolCallServer`), and the name the model called it by (`toolCallOfferedName` —
* a built-in's own name, an MCP tool's model-facing `<server>__<tool>` spelling).
*
* The source is the request's own record when the log has one — its
* `span.model_request_start.tools` list — and the event's own type otherwise, which is how an
* MCP call a client joined mid-request still says `mcp` rather than being read as a built-in.
*
* The status is derived rather than stored in the log: a call whose policy is `ask` (which is
* every MCP tool's default, #312) is `waiting` — nothing runs it until the reader answers — and
* any other call is `running` while the turn is working, or `lost` once the turn is over and
* nothing has answered it, which only a call the brain never got to run can be.
*/
function fromToolUse(state, event) {
	const name = toolCallOfferedName(event);
	const server = toolCallServer(event);
	const permission = toolCallPermission(event);
	if (isTaskToolName(name)) return withTaskEvents(state, event);
	return withTodoEvents(upsertToolCall(state, {
		id: toolCallId(event),
		name,
		input: toolCallInput(event),
		permission,
		source: state.toolSources[name] ?? toolCallSource(event),
		...server === void 0 ? {} : { server },
		status: toolCallStatus(permission, void 0, {
			waiting: false,
			running: state.status === "running"
		}),
		position: event.seq
	}), event);
}
/**
* Fold a tool result in: what answered the call, and the status it moves to (epic #303, X1;
* #308; #312).
*
* One fold for both pairs. The result names its call through the protocol's id reading
* (`toolResultCallId`, the MCP pair's `mcp_tool_use_id` against the built-in's `tool_use_id`);
* a result for a call this client never saw — it joined mid-step, or a rewind took the call
* back — has nothing to attach to and is dropped.
*/
function fromToolResult(state, event) {
	const callId = toolResultCallId(event);
	const index = state.toolCalls.findIndex((call) => call.id === callId);
	if (index === -1) return state;
	const existing = state.toolCalls[index];
	const result = {
		content: event.content.map((block) => block.text).join(""),
		isError: toolResultIsError(event)
	};
	const sources = event.type === EVENT_TYPES.agentToolResult ? event.sources ?? [] : [];
	const call = {
		...existing,
		...sources.length === 0 ? {} : { sources },
		result,
		status: toolCallStatus(existing.permission, result, {
			waiting: false,
			running: state.status === "running"
		})
	};
	const toolCalls = state.toolCalls.slice();
	toolCalls[index] = call;
	return withTodoEvents({
		...state,
		toolCalls
	}, event);
}
/**
* Keep the reader's decision about a waiting call (epic #303, X6; #309; #310).
*
* The stored `user.tool_confirmation` is the record, so this is a plain append keyed by the
* call it names: the newest confirmation for a call is the answer, exactly as the brain's
* `confirmationsByCall` reads it. A confirmation for a call the transcript never saw — it
* joined mid-step — is still kept: the decision happened, and the call it names may arrive
* later in the same replay.
*/
function recordConfirmation(state, event) {
	const confirmation = {
		toolUseId: event.tool_use_id,
		result: event.result,
		...event.remember === void 0 ? {} : { remember: event.remember },
		...event.deny_message === void 0 ? {} : { denyMessage: event.deny_message },
		...event.answers === void 0 ? {} : { answers: event.answers },
		seq: event.seq
	};
	const replaceable = state.confirmations.findIndex((current) => current.seq === event.seq || current.toolUseId === confirmation.toolUseId);
	if (replaceable === -1) return {
		...state,
		confirmations: [...state.confirmations, confirmation]
	};
	const confirmations = state.confirmations.slice();
	confirmations[replaceable] = confirmation;
	return {
		...state,
		confirmations
	};
}
/**
* Record a tool event for the todo list, and recompute the list it holds (epic #303, #308).
*
* Only `todo_write`'s own calls matter to {@link readTodoList}, but keeping every tool event is
* what lets the protocol's rule — the newest successful call wins, a failed or unanswered one
* counts for nothing — be the one that runs, rather than a restatement of it here. The list is
* recomputed from the events, so a call and the result that answers it move it in one step.
*/
function withTodoEvents(state, event) {
	const todoEvents = [...state.todoEvents, event];
	return {
		...state,
		todoEvents,
		todos: readTodoList(todoEvents)
	};
}
/**
* Record an event the tasks are read from, and recompute the cards it holds (epic #340; #345).
*
* The rule the todo list follows: `tasksFromEvents` is a pure function over the events, so the
* transcript keeps them and asks it, rather than restating "the newest status wins" and "the
* newest outcome is the result" here — where a rewind would then have to restate them too.
*/
function withTaskEvents(state, event) {
	const taskEvents = [...state.taskEvents, event];
	return {
		...state,
		taskEvents,
		tasks: tasksFromEvents(taskEvents)
	};
}
/**
* Recompute every call's status for a turn that started or ended (epic #303, #308).
*
* Only the calls a result never answered change: one is `running` while the turn works and
* `lost` once it is over, while a call waiting on the reader keeps `waiting` (its permission is
* `ask`). Returns the list it was given when nothing changed, so the state keeps its identity.
*/
function refreshToolStatuses(state, status) {
	let changed = false;
	const toolCalls = state.toolCalls.map((call) => {
		const next = toolCallStatus(call.permission, call.result, {
			waiting: false,
			running: status === "running"
		});
		if (next === call.status) return call;
		changed = true;
		return {
			...call,
			status: next
		};
	});
	return changed ? toolCalls : state.toolCalls;
}
/**
* Put `call` in the transcript: replacing the call with the same id, or inserting it by
* position, exactly as {@link upsertMessage} does for messages.
*/
function upsertToolCall(state, call) {
	const existing = state.toolCalls.find((candidate) => candidate.id === call.id);
	if (existing !== void 0 && isSameToolCall(existing, call)) return state;
	const rest = state.toolCalls.filter((candidate) => candidate.id !== call.id);
	const index = rest.findIndex((candidate) => candidate.position > call.position);
	const toolCalls = index === -1 ? [...rest, call] : [
		...rest.slice(0, index),
		call,
		...rest.slice(index)
	];
	return {
		...state,
		toolCalls
	};
}
/** Whether an upsert would change nothing, so the state can keep its identity. */
function isSameToolCall(current, next) {
	return current.name === next.name && current.permission === next.permission && current.source === next.source && current.server === next.server && current.status === next.status && current.position === next.position && sameJson(current.input, next.input) && current.result?.content === next.result?.content && current.result?.isError === next.result?.isError && sameJson(current.sources ?? null, next.sources ?? null);
}
/** Whether two JSON inputs are the same value, compared structurally. */
function sameJson(current, next) {
	return JSON.stringify(current) === JSON.stringify(next);
}
/**
* Fold a stored `user.message` in, applying the model-switch rule (epic #116, U1).
*
* A message that carries a `model` switches the session to it: the state's `model` moves to
* the new id, and the message carries {@link TranscriptMessage.modelChangedTo} when that id
* differs from the one already in effect — a change a UI marks. What is "already in effect" is
* whatever `state.model` held: the session's own model when the frontend seeded it (#268), the
* model the last request ran, or the `null` of a transcript nothing has told anything. A
* message naming the model already in effect is left unmarked.
*/
function fromUserMessage(state, event) {
	const model = event.model?.id;
	const parts = textParts(event.content.map((block) => block.text));
	const skill = event.skill;
	return {
		...upsertMessage(state, {
			id: event.id,
			role: "user",
			parts,
			text: joinedText(parts),
			pending: event.processed_at === null,
			streaming: false,
			position: event.seq,
			...model !== void 0 && state.model !== null && model !== state.model ? { modelChangedTo: model } : {},
			...skill === void 0 ? {} : { skill: {
				name: skill.name,
				arguments: skill.arguments ?? null
			} }
		}),
		model: model ?? state.model
	};
}
/**
* Fold a stored `agent.message` in: the reply, plus the model requests it answers (epic #201,
* U1).
*
* The reply takes every request the turn has opened and no earlier reply claimed — the failed
* attempt a retry followed, and the retry itself — as its {@link TranscriptMessage.meta}. The
* requests keep the message's id, so the span ends still to come know which reply to add their
* tokens and their duration to: the span end of a reply arrives *after* the reply itself.
*/
function fromAgentMessage(state, event) {
	const claimed = state.pendingRequests.filter((request) => request.messageId === void 0 && request.summary !== true);
	const message = messageFromAgentEvent(event, agentMessagePosition(state, event), metaFrom(claimed));
	const pendingRequests = [...state.pendingRequests.filter((request) => request.messageId !== void 0), ...claimed.map((request) => ({
		...request,
		messageId: event.id
	}))];
	return {
		...upsertMessage(state, message),
		lastError: null,
		pendingRequests
	};
}
/**
* Fold a stored `session.context_summary` in: the divider, and the reduced context it leaves
* behind (epic #277, K10; #280).
*
* The summary supersedes nothing, so the messages stay exactly as they were — the divider is a
* mark *in* the conversation, at the last `seq` the summary covers, and the history above it
* stays on screen. What changes is what the meter measures: from here until the next real
* request reports its own size, the context is an estimate (`contextAfterSummary`) built from
* the summary and the messages it replaced.
*
* The progress line goes with it: the compaction this event is the end of is over.
*/
function fromContextSummary(state, event) {
	const summary = {
		id: event.id,
		summary: event.summary,
		reason: event.reason,
		model: event.summary_model,
		passes: event.passes,
		tokensBefore: event.tokens_before,
		...event.fallback_reason === void 0 ? {} : { fallbackReason: event.fallback_reason },
		position: event.covers.to_seq,
		seq: event.seq
	};
	return {
		...state,
		summaries: [...state.summaries.filter((each) => each.seq !== event.seq), summary],
		summarizing: null,
		context: contextAfterSummaryEvent(state, event)
	};
}
/**
* The context size a summary leaves: the last measured prompt less what the summary replaced,
* plus the summary's own text (epic #277, K10; #280).
*
* The baseline is the last real request's measurement when the transcript has one — it is a
* measurement of the same conversation, which is what makes subtracting from it meaningful — and
* the summary's own `tokens_before` when it does not (a session whose first request is the one
* the summary was made for). The covered text comes from the messages the transcript holds, at
* the same estimate; the framing the real prompt paid for is not in it, so the estimate errs
* high rather than low.
*/
function contextAfterSummaryEvent(state, event) {
	const coveredText = state.messages.filter((message) => message.position <= event.covers.to_seq).map((message) => message.text).join("\n");
	return {
		tokens: contextAfterSummary({
			baseline: state.context?.tokens ?? event.tokens_before,
			summary: event.summary,
			coveredText
		}),
		estimated: true
	};
}
/** The transcript message for a stored `agent.message`, reconciled with any preview of it. */
function messageFromAgentEvent(event, position, meta) {
	const parts = textParts(event.content.map((block) => block.text));
	return {
		id: event.id,
		role: "agent",
		parts,
		text: joinedText(parts),
		pending: false,
		streaming: false,
		position,
		...meta === void 0 ? {} : { meta }
	};
}
/** The text parts of a stored message: v1's content blocks are text only. */
function textParts(blocks) {
	return blocks.map((text) => ({
		type: "text",
		text
	}));
}
/** A message's text: its text parts, joined. */
function joinedText(parts) {
	return parts.filter((part) => part.type === "text").map((part) => part.text).join("");
}
/** The request a `span.model_request_start` opens (epic #201, U1). */
function openedRequest(event) {
	const startedAt = epochMs(event.processed_at);
	return {
		id: event.id,
		...event.model === void 0 ? {} : { model: event.model },
		...startedAt === void 0 ? {} : { startedAt }
	};
}
/**
* Close the request a `span.model_request_end` names, and fold what it reported into the reply
* it belongs to (epic #201, U1).
*
* The span end arrives *after* the reply it produced, which is why the metadata is written
* here rather than when the reply lands: the end carries the tokens and the time. A request
* that no reply has claimed yet — a failed attempt the brain then retried — is only marked
* ended here; it stays tracked so the reply the retry produces sums both requests. A span end
* whose start the client never saw (it joined mid-request) still opens an entry, so a reply
* the turn stores after it takes those tokens; one stored *before* it is left alone, because
* the end names the request that opened it and a client that missed that event has nothing to
* tie the end to.
*/
function closeRequest(state, event) {
	const existing = state.pendingRequests.find((request) => request.id === event.model_request_start_id);
	const endedAt = epochMs(event.processed_at);
	const closed = {
		...existing ?? { id: event.model_request_start_id },
		...endedAt === void 0 ? {} : { endedAt },
		usage: usageFrom(event.model_usage)
	};
	const measured = existing?.summary === true ? null : promptTokens(closed.usage);
	const measured_ = measured === null ? state : {
		...state,
		context: {
			tokens: measured,
			estimated: false
		}
	};
	const requests = existing === void 0 ? [...state.pendingRequests, closed] : state.pendingRequests.map((request) => request.id === closed.id ? closed : request);
	const messageId = closed.messageId;
	if (messageId === void 0) return {
		...measured_,
		pendingRequests: requests
	};
	const attributed = requests.filter((request) => request.messageId === messageId);
	return withMeta({
		...measured_,
		pendingRequests: requests.filter((request) => request.id !== closed.id)
	}, messageId, metaFrom(attributed));
}
/**
* The real prompt size a request reported, or `null` when it reported none.
*
* The three input-side counters summed — the same measure the brain's `promptTokensOf` takes
* (epic #277, K2), because the four counters are disjoint. A sum of zero is not a measurement:
* a prompt is never empty, so a zero is a failed attempt's placeholder.
*/
function promptTokens(usage) {
	if (usage === void 0) return null;
	const tokens = usage.input + usage.cacheCreation + usage.cacheRead;
	return tokens > 0 ? tokens : null;
}
/** Write `meta` onto the reply `id`, when it is a message the transcript holds. */
function withMeta(state, id, meta) {
	const message = meta === void 0 ? void 0 : state.messages.find((each) => each.id === id);
	return message === void 0 ? state : upsertMessage(state, {
		...message,
		meta
	});
}
/**
* The metadata a set of model requests adds up to, or `undefined` when they say nothing.
*
* The model is the last one they named — a reply that took several requests ran on the model
* that finally answered it — and the duration runs from the first request's start to the last
* one's end, so a retried reply reports the time it really took. Tokens are summed over the
* requests that reported any, which is how a retried reply's cost includes the attempt that
* failed before it.
*/
function metaFrom(requests) {
	const model = requests.reduce((last, request) => request.model ?? last, void 0);
	const usage = summedUsage(requests);
	const durationMs = spanMs(requests);
	if (model === void 0 && usage === void 0 && durationMs === void 0) return;
	return {
		...model === void 0 ? {} : { model },
		...durationMs === void 0 ? {} : { durationMs },
		...usage === void 0 ? {} : { usage }
	};
}
/** The tokens the requests reported, summed; `undefined` when not one of them reported any. */
function summedUsage(requests) {
	let input = 0;
	let output = 0;
	let cacheCreation = 0;
	let cacheRead = 0;
	let reported = false;
	for (const request of requests) {
		if (request.usage === void 0) continue;
		input += request.usage.input;
		output += request.usage.output;
		cacheCreation += request.usage.cacheCreation;
		cacheRead += request.usage.cacheRead;
		reported = true;
	}
	return reported ? {
		input,
		output,
		cacheCreation,
		cacheRead,
		total: input + output
	} : void 0;
}
/**
* How long the requests took, from the earliest start to the latest end.
*
* `undefined` when the log does not say — a client that joined after the start, a request
* whose span end never arrived — and for a negative span: `processed_at` is not monotonic
* across a crash (D9), and a reply that took less than no time is not a duration a UI can
* print.
*/
function spanMs(requests) {
	const starts = requests.flatMap((request) => request.startedAt === void 0 ? [] : [request.startedAt]);
	const ends = requests.flatMap((request) => request.endedAt === void 0 ? [] : [request.endedAt]);
	if (starts.length === 0 || ends.length === 0) return;
	const elapsed = Math.max(...ends) - Math.min(...starts);
	return elapsed < 0 ? void 0 : elapsed;
}
/** The tokens a `span.model_request_end` reported, in the shape a message's metadata takes. */
function usageFrom(usage) {
	return {
		input: usage.input_tokens,
		output: usage.output_tokens,
		cacheCreation: usage.cache_creation_input_tokens,
		cacheRead: usage.cache_read_input_tokens,
		total: usage.input_tokens + usage.output_tokens
	};
}
/** A `session.usage` event in the shape the transcript keeps (#247). */
function usageFromEvent(event) {
	return {
		totals: totalsOf(event),
		models: event.models.map((entry) => ({
			model: entry.model,
			usage: totalsOf(entry.usage),
			requests: entry.requests
		}))
	};
}
/** The four counters of a usage report, in the transcript's spelling. */
function totalsOf(usage) {
	return {
		input: usage.input_tokens,
		output: usage.output_tokens,
		cacheCreation: usage.cache_creation_input_tokens,
		cacheRead: usage.cache_read_input_tokens
	};
}
/** A timestamp as epoch milliseconds, or `undefined` when it is not a date at all. */
function epochMs(timestamp) {
	const ms = Date.parse(timestamp);
	return Number.isNaN(ms) ? void 0 : ms;
}
/**
* Where a stored `agent.message` sorts.
*
* `supersedes.from_seq` when it carries the range: that is where the reply started, whatever
* the client saw of its chunks — a client that joined mid-reply, one whose chunks were
* deleted, and one that watched every delta all sort the reply identically (D9). With no
* range — a server from before D9, or a reply whose chunks were never stored — it keeps the
* position of the preview it replaces, where the bubble opened; and a message that replaces
* nothing lands at its own `seq`.
*/
function agentMessagePosition(state, event) {
	if (event.supersedes !== void 0) return event.supersedes.from_seq;
	return state.messages.find((message) => message.id === event.id)?.position ?? event.seq;
}
/**
* Open the preview of `id` at `position`, unless a message with that id is already final.
*/
function startPreview(state, id, position) {
	return isPreviewable(state, id) ? upsertMessage(state, emptyPreview(id, position)) : state;
}
/**
* Whether a preview may still write to the message `id`.
*
* True when nothing carries that id yet, or when what does is itself still a preview.
*/
function isPreviewable(state, id) {
	const existing = state.messages.find((message) => message.id === id);
	return existing === void 0 || existing.streaming;
}
/** A preview of `id` with no text yet, so a UI can show that the reply has started. */
function emptyPreview(id, position) {
	return {
		id,
		role: "agent",
		parts: [],
		text: "",
		pending: false,
		streaming: true,
		position
	};
}
/**
* Put `message` in the transcript: replacing the message with the same id, or inserting it
* among the others by position.
*
* Replacing is what reconciles a preview with the stored event that supersedes it, and what
* makes a re-delivered event a no-op rather than a duplicate. `messages` stays sorted by
* position, so a stored reply that carries `supersedes` moves back to where it started —
* ahead of a steering message it was interleaved with — and every client renders the same
* conversation whether it followed the reply's chunks or joined afterwards.
*/
function upsertMessage(state, message) {
	const existing = state.messages.find((candidate) => candidate.id === message.id);
	if (existing !== void 0 && isSameMessage(existing, message)) return state;
	const rest = state.messages.filter((candidate) => candidate.id !== message.id);
	const index = rest.findIndex((candidate) => candidate.position > message.position);
	const messages = index === -1 ? [...rest, message] : [
		...rest.slice(0, index),
		message,
		...rest.slice(index)
	];
	return {
		...state,
		messages
	};
}
/**
* Whether an upsert would change nothing, so the state can keep its identity.
*
* `text` is compared through `parts` — it is their text, joined — so a message whose metadata
* a span end filled in is a change and one a replayed event restates is not.
*/
function isSameMessage(current, next) {
	return current !== void 0 && sameParts(current.parts, next.parts) && current.pending === next.pending && current.streaming === next.streaming && current.position === next.position && current.role === next.role && sameMeta(current.meta, next.meta) && sameSkill(current.skill, next.skill);
}
/** Whether two messages invoke the same skill the same way. */
function sameSkill(current, next) {
	return current === next || current !== void 0 && next !== void 0 && current.name === next.name && current.arguments === next.arguments;
}
/** Whether two part lists render the same. */
function sameParts(current, next) {
	return current.length === next.length && current.every((part, index) => {
		const other = next[index];
		return other !== void 0 && other.type === part.type && other.text === part.text;
	});
}
/** Whether two replies report the same metadata. */
function sameMeta(current, next) {
	if (current === next) return true;
	return current !== void 0 && next !== void 0 && current.model === next.model && current.durationMs === next.durationMs && current.usage?.input === next.usage?.input && current.usage?.output === next.usage?.output && current.usage?.cacheCreation === next.usage?.cacheCreation && current.usage?.cacheRead === next.usage?.cacheRead && current.usage?.total === next.usage?.total;
}
/**
* Extend a preview with a delta.
*
* Deltas carry the index of the content block they extend, so they accumulate per index and
* `text` is the parts in order — the same string the stored event will carry once it
* replaces the preview. A delta for an event whose `event_start` was missed (a connection
* that opened mid-reply) still lands: it opens the preview itself, at `createPosition` —
* the delta's own `seq`. A delta for a message that is already stored changes nothing.
*
* @param state the transcript so far
* @param eventId the id of the event being previewed
* @param index the content block the delta extends
* @param text the fragment to append
* @param createPosition where to open the preview, if there is none yet
*/
function appendDelta(state, eventId, index, text, createPosition) {
	if (!isPreviewable(state, eventId)) return state;
	const current = state.messages.find((message) => message.id === eventId) ?? emptyPreview(eventId, createPosition);
	const parts = current.parts.slice();
	parts[index] = {
		type: "text",
		text: (parts[index]?.text ?? "") + text
	};
	return upsertMessage(state, {
		...current,
		parts,
		text: joinedText(parts),
		streaming: true
	});
}
/** The messages, in order. */
function selectMessages(state) {
	return state.messages;
}
/** Whether the agent is working. */
function selectIsRunning(state) {
	return state.status === "running";
}
/** The last message, or `null` in an empty transcript. */
function selectLastMessage(state) {
	return state.messages.at(-1) ?? null;
}
/** The `agent.message` being previewed right now, or `null`. */
function selectStreamingMessage(state) {
	return state.messages.find((message) => message.streaming) ?? null;
}
/**
* The summaries the conversation still holds, in the order they were written (epic #277, K10;
* #280).
*
* Each one's {@link TranscriptSummary.position} is where its divider belongs among the messages,
* so a transcript that renders both can interleave them from this list alone.
*/
function selectSummaries(state) {
	return state.summaries;
}
/**
* The conversation and its summary dividers as one ordered list (epic #277, K10; #280).
*
* The order is by position, and a divider draws **after** the message it covers — a summary
* marks where the model stops reading verbatim, which is past the event at `covers.to_seq`, not
* before it. `messages` and `summaries` are both kept in ascending position by the reducer, so
* this is one merge walk.
*
* A frontend renders this through a lookup on `kind` (the shape `PART_RENDERERS` has), so a
* transcript cannot end up drawing its dividers in a different place from the other frontend's.
*/
function selectTranscriptEntries(state) {
	return transcriptEntries(state.messages, state.summaries, state.toolCalls);
}
/**
* The same merge, for a caller that holds the lists without a transcript around them.
*
* A tie goes to the message, then to the tool call, then to the divider: a summary draws
* **after** the event it covers, and a call is drawn where its own event sits — never at a
* position a message already occupies, since every event has one `seq` of its own.
*
* @param messages the conversation, in position order
* @param summaries the dividers, in position order
* @param toolCalls the calls, in position order
*/
function transcriptEntries(messages, summaries, toolCalls = []) {
	const entries = [];
	let messageIndex = 0;
	let summaryIndex = 0;
	let toolIndex = 0;
	while (messageIndex < messages.length || summaryIndex < summaries.length || toolIndex < toolCalls.length) {
		const message = messages[messageIndex];
		const summary = summaries[summaryIndex];
		const call = toolCalls[toolIndex];
		const nextMessage = message?.position ?? Number.POSITIVE_INFINITY;
		const nextTool = call?.position ?? Number.POSITIVE_INFINITY;
		const nextSummary = summary?.position ?? Number.POSITIVE_INFINITY;
		const min = Math.min(nextMessage, nextTool, nextSummary);
		if (nextMessage === min) {
			entries.push({
				kind: "message",
				message
			});
			messageIndex += 1;
			continue;
		}
		if (nextTool === min) {
			entries.push({
				kind: "tool",
				call
			});
			toolIndex += 1;
			continue;
		}
		entries.push({
			kind: "summary",
			summary
		});
		summaryIndex += 1;
	}
	return entries;
}
/**
* The tool calls the conversation holds, in position order (epic #303, X5; #308).
*
* A frontend that renders {@link selectTranscriptEntries} gets them interleaved with the
* messages; this is for one that wants the calls on their own (a count, a "waiting" badge).
*/
function selectToolCalls(state) {
	return state.toolCalls;
}
/**
* The decisions the reader made about calls that waited on them, in log order (epic #303,
* X6; #309; #310).
*
* A frontend reads {@link selectConfirmation} for one call's own answer; this is for one that
* wants them all (a count, an audit line).
*/
function selectConfirmations(state) {
	return state.confirmations;
}
/**
* The reader's decision about one call, or `null` (epic #303, X6; #309; #310).
*
* The newest confirmation naming the call is the answer, which is the same rule the brain
* reads a `remember: session` approval back with — one lookup, so a UI and the brain cannot
* disagree about what was decided.
*
* @param state the transcript
* @param toolUseId the call's id, the `agent.tool_use` event's own
*/
function selectConfirmation(state, toolUseId) {
	let found = null;
	for (const confirmation of state.confirmations) if (confirmation.toolUseId === toolUseId) found = confirmation;
	return found;
}
/**
* The newest MCP server a turn could not use, or `null` (epic #303, X10; #313).
*
* It is **not** `lastError`: the notice a missing tool source is owed outlives the reply that
* follows it (see {@link TranscriptMcpFailure}), so a screen reads this rather than the
* transcript's newest error. It is `null` — not a guess — when nothing has failed, and it is
* dropped only by a rewind over the event that reported it.
*
* @param state the transcript
*/
function selectMcpFailure(state) {
	return state.mcpFailure;
}
/**
* The tool results the newest real request had to shorten (epic #303, X9; #306; #308).
*
* Empty when the request capped nothing — which is every turn that did not fill a tool's head
* room, and every log stored before #306.
*/
function selectTruncatedToolResults(state) {
	return state.truncatedToolResults;
}
/** The old tool results the newest real request cleared, or `null` (epic #303, X9; #306; #308). */
function selectClearedToolResults(state) {
	return state.clearedToolResults;
}
/**
* How many searches this chat's calls add up to (epic #303, X5; #305; #308).
*
* The same count the usage routes report, for a screen that already holds the calls — so the
* chat header can say what the chat searched for without a second request. `searchCount` is
* where the counting rule lives, shared with whatever else draws it.
*/
function selectSearchCount(state) {
	return searchCount(state.toolCalls);
}
/**
* The task list the model last wrote with `todo_write`, or `null` (epic #303, X5; #305; #308).
*
* Both frontends draw it while a chat has one — a pinned panel on the web, a compact block in
* `oh` — and it updates live, because it is recomputed from the calls as they land. `null` is "no
* list has ever been written"; an empty array is a model clearing the one it had.
*/
function selectTodos(state) {
	return state.todos;
}
/**
* The background tasks the conversation holds, in card order (epic #340, S9; issue #345).
*
* A frontend draws one card per task — the type and the description, the status, the messages
* exchanged and the result — and merges the list with {@link selectTranscriptEntries} by
* {@link TaskCard.position}, which is the `seq` of the `agent` call the card stands in for.
* The status here is the parent's log's; `waiting` comes from the child's own stream through
* `taskCardStatus` (`./tasks`).
*/
function selectTasks(state) {
	return state.tasks;
}
/** The summary being written right now, or `null` (epic #277, C2/K10; #280). */
function selectSummarizing(state) {
	return state.summarizing;
}
/**
* How full the context was at the last real model request, or `null` (epic #277, K2/K10; #280).
*
* What {@link contextMeter} turns into the number a frontend draws, together with the current
* model's budget and the caller's threshold.
*/
function selectContext(state) {
	return state.context;
}
/** The newest item a request had to shorten to fit, or `null` (epic #277, K6/K10; #280). */
function selectTruncation(state) {
	return state.truncation;
}
/**
* The manual compaction the log last asked for, or `null` (epic #277, K8; #283).
*
* What a frontend draws the ask and its outcome from: `pending` is the "Compacting…" state, and
* the outcome is a notice for the two results a reader has to be told about
* ({@link manualCompactionNotice} turns it into the words).
*/
function selectManualCompaction(state) {
	return state.manualCompaction;
}
/**
* The session's usage: the running totals the log reported, or the same totals derived from
* its replies (epic #245, A2; issue #247).
*
* **A session stored before `session.usage` existed derives its totals here**, which is what
* makes a replay equal a live stream: the newest event's totals are exactly what a fold over
* the stored `span.model_request_end` events produces, and the fold is what a transcript built
* from those spans already holds. The event is preferred when there is one because it is the
* whole answer in one place — and because it also counts a request that produced no reply.
*
* The derivation attributes each reply to the model its metadata names. A reply whose model
* the log does not name — a client that joined mid-request — contributes to no entry, so the
* totals are always the models' sum and a reader never sees a total it cannot break down.
*/
function selectSessionUsage(state) {
	return state.usage ?? sessionUsageOf(state.messages);
}
/**
* The totals a transcript's replies add up to, per model.
*
* The derivation {@link selectSessionUsage} falls back to; exported for a caller that holds
* messages without a transcript around them.
*
* @param messages the conversation, in order
*/
function sessionUsageOf(messages) {
	const models = /* @__PURE__ */ new Map();
	const requests = /* @__PURE__ */ new Map();
	let totals = emptyTotals();
	for (const message of messages) {
		const model = message.meta?.model;
		const usage = message.meta?.usage;
		if (model === void 0 || usage === void 0) continue;
		totals = plus(totals, usage);
		models.set(model, plus(models.get(model) ?? emptyTotals(), usage));
		requests.set(model, (requests.get(model) ?? 0) + 1);
	}
	return {
		totals,
		models: [...models].map(([model, usage]) => ({
			model,
			usage,
			requests: requests.get(model) ?? 0
		}))
	};
}
/**
* What a session's tokens cost: the sum of the requests that could be priced, and how many could
* not (epic #245, A2; #247, decided 2026-10-09).
*
* `prices` is the model catalog's (`ModelEntry.cost`). A model it does not price makes its
* requests — all of them, which the totals count per model — unpriced rather than free: the cost
* is the priced part, and `unpriced_requests` names the rest, which is what a frontend renders as
* "+ N unpriced". The cost is `null` only when nothing in the session could be priced, which is
* what "—" means.
*
* @param usage the session's totals, as {@link selectSessionUsage} answers them
* @param prices the price of a `provider/model` id, or `null` for one nobody publishes
*/
function sessionCost(usage, prices) {
	const parts = usage.models.flatMap((entry) => {
		const cost = totalsCost(entry.usage, prices(entry.model));
		return cost === null ? Array(entry.requests).fill(null) : [cost];
	});
	return totalCost(parts);
}
/**
* What one reply cost, in USD — or `null` when the log or the catalog cannot say (epic #245,
* A2; issue #247).
*
* A reply with no metadata, no model, or no tokens has no cost to report: `null`, which a
* frontend draws as "—" beside the tokens it does have.
*
* @param meta the reply's metadata, from {@link TranscriptMessage.meta}
* @param prices the price of a `provider/model` id, or `null` for one nobody publishes
*/
function replyCost(meta, prices) {
	if (meta?.usage === void 0) return null;
	return totalsCost(meta.usage, meta.model === void 0 ? null : prices(meta.model));
}
/** The cost of a set of counters at one model's rates, via the protocol's arithmetic. */
function totalsCost(usage, cost) {
	return usageCost({
		input_tokens: usage.input,
		output_tokens: usage.output,
		cache_creation_input_tokens: usage.cacheCreation,
		cache_read_input_tokens: usage.cacheRead
	}, cost);
}
/** The four counters at zero. */
function emptyTotals() {
	return {
		input: 0,
		output: 0,
		cacheCreation: 0,
		cacheRead: 0
	};
}
/** A reply's tokens added into a running total, counter by counter. */
function plus(into, usage) {
	return {
		input: into.input + usage.input,
		output: into.output + usage.output,
		cacheCreation: into.cacheCreation + usage.cacheCreation,
		cacheRead: into.cacheRead + usage.cacheRead
	};
}
/**
* Create a transcript store.
*
* The reducer functions are exported on their own for callers that would rather hold the
* state themselves (a reducer in a framework, a test asserting on one event); this is the
* wrapper that keeps it.
*
* @param initial starting state; defaults to {@link initialTranscriptState}
*/
function createTranscript(initial = initialTranscriptState()) {
	let state = initial;
	const listeners = /* @__PURE__ */ new Set();
	const setState = (next) => {
		state = next;
		for (const listener of listeners) listener(state);
		return state;
	};
	return {
		getState: () => state,
		apply: (event) => setState(reduceTranscript(state, event)),
		applyAll: (events) => setState(reduceTranscriptAll(state, events)),
		reset: (seed) => setState(initialTranscriptState(seed)),
		subscribe(listener) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		}
	};
}
//#endregion
//#region src/tool-settings.ts
/**
* The settings view of a tool: which permission it may take, what a cycling key does, and the
* words a row is drawn with (epic #303, X4; #335).
*
* The web app's Settings → Tools card and the terminal's `/tools` screen ask the same three
* questions about every tool — is it on, what permission does a call run under, and is it
* available to this chat at all — and they have to answer them the same way. The pieces that
* are not just the wire's vocabulary live here: the cycle a permission key walks, which is
* **not** the same for every tool, and the two notes a row owes the reader.
*
* Everything is a plain function of an entry, so the rules are tested without a screen.
*/
/**
* The tools whose calls **cannot pause for the reader** (epic #340, S9; issue #368).
*
* `ask` is the pause #309 answers: the call is stored, nothing runs it, and the turn ends
* `requires_action` until one `user.tool_confirmation` answers it. That needs a client that can
* *draw* the prompt, and these four are deliberately kept out of the transcript's `toolCalls`
* (S9) — a card stands in for the `agent` call, and the other three are task machinery rather
* than work worth a line — so `pendingCalls(toolCalls)` holds nothing for either frontend to
* hang a prompt under. A policy of `ask` on one of them paused the parent's turn with no way to
* answer it, reachable from Settings → Tools, which listed them like any other built-in.
*
* The decision (2026-10-13) is that these tools **do not support `ask`**, and the server refuses
* the write; this list is the frontends' half of it, so a settings screen never offers a switch
* the save would reject. `agent_wait` is **not** here: its own pause (waiting for the tasks it
* names) outranks the permission, and a `deny` on it is still refused by the loop.
*/
const NO_ASK_TOOL_NAMES = [
	AGENT_TOOL_NAME,
	AGENT_MESSAGE_TOOL_NAME,
	AGENT_STOP_TOOL_NAME,
	MESSAGE_PARENT_TOOL_NAME
];
/**
* Whether `ask` is a permission this tool may take.
*
* False for the two kinds of tool that cannot pause: a **provider-executed** one (`web_search`
* runs inside the model request, so there is no moment at which this build could ask — the
* server refuses the write, #335) and the four **subagent** ones ({@link NO_ASK_TOOL_NAMES},
* whose calls are not in `toolCalls` and so have no prompt a client could draw).
*
* @param name the offered name — a built-in's own, or a remote tool's `<server>__<tool>`
*/
function toolSupportsAsk(name) {
	return name !== WEB_SEARCH_TOOL_NAME && !NO_ASK_TOOL_NAMES.includes(name);
}
/**
* What a row says instead of offering `ask` for a tool that cannot take it (issue #368).
*
* Short, and it names the reason rather than the rule: a reader who wanted "pause and let me
* decide" needs to know there is nobody to pause for, not that this build dislikes the value.
*/
const SUBAGENT_ASK_NOTE = "Subagents run in the background — allow or deny it.";
/**
* The permissions a cycling key walks for a tool, in order.
*
* `ask` is missing for a tool that cannot pause — see {@link toolSupportsAsk}. Leaving it in the
* cycle would offer a reader a switch that the save then rejects (a search) or one that leaves the
* turn paused with nothing to answer it (a subagent call); `allow` → `deny` is the whole of what
* cycling one of those means.
*/
function toolPolicyChoices(name) {
	return toolSupportsAsk(name) ? TOOL_POLICIES : TOOL_POLICIES.filter((p) => p !== "ask");
}
/**
* Where a cycle key takes a tool's permission: `current` moved by `delta` (usually ±1), through
* {@link toolPolicyChoices} and wrapping.
*
* `null` is "no permission to start from" — a row whose tools disagree (the `/mcp` tools face's
* mixed state) — and the walk then starts **at the end it is walked from**: a forward step takes
* the first choice and a backward one the last, so a first press lands on a permission that is
* really written rather than on an arbitrary one. A `current` the tool may not take (a stored
* `ask` on `web_search`, which the server reads back as `allow`) is treated the same way,
* because the value a reader sees is the one the write will leave behind.
*/
function cycleToolPolicy(name, current, delta) {
	const choices = toolPolicyChoices(name);
	const at = current === null ? -1 : choices.indexOf(current);
	if (at === -1) return (delta >= 0 ? choices[0] : choices[choices.length - 1]) ?? "allow";
	return choices[(at + delta + choices.length) % choices.length] ?? choices[0] ?? "allow";
}
/**
* The permission column of a tool's row: the permission, and the default it agrees with.
*
* `allow` alone when the tool's own declaration is `allow` (the permission in force **is** the
* default, so there is nothing to say), `deny (default ask)` when the reader chose against the
* declaration, and `ask (default)` when they chose the one it happens to equal. A tool this
* deployment does not register (`default_policy: null`) has no declaration to agree with, so it
* reads as itself.
*
* The rule lives here rather than in each screen because the shell's `oh tools` and the chat's
* `/tools` must not come to spell the same permission differently.
*/
function toolPolicyNote(entry) {
	if (entry.default_policy === null) return entry.policy;
	return entry.policy === entry.default_policy ? `${entry.policy} (default)` : `${entry.policy} (default ${entry.default_policy})`;
}
/**
* Why a tool cannot be used here, or `null` when it can.
*
* The server's own `reason` where it sent one — `This model has no web search.` is the sentence
* a reader gets when their chat's model has no native search — and the plain note that this
* deployment does not register the tool otherwise. `available: false` has more than one cause
* and a settings screen owes the reader the one that applies rather than a single phrase for
* both.
*/
function toolAvailabilityNote(entry) {
	if (entry.available) return null;
	return entry.reason ?? "not available on this server";
}
/** What an unavailable tool is called when the server named no reason for it. */
const NOT_REGISTERED_NOTE = "not available on this server";
//#endregion
//#region src/skills.ts
/**
* Every built-in command the `/` menu offers, in the order it lists them.
*
* `slashCommands` puts these rows above the reader's skills, so the commands that always exist
* are in a stable place and the skill rows grow under them. The names are all members of
* `RESERVED_SKILL_NAMES` — a test holds the list to it — so a built-in can never collide with
* an importable skill name.
*/
const BUILTIN_COMMANDS = [{
	name: "compact",
	usage: "/compact [instructions]",
	description: "Summarize the older history now"
}];
/**
* The whole `/` menu: the built-in commands, then the reader's own usable skills (K7).
*
* A skill is offered when it is **on in this chat** and **user-invocable** (K1, K8) — a skill
* whose frontmatter says `user-invocable: false` is one only the model uses, so it is not a
* command the reader can type and never appears here. `disable-model-invocation` is the other
* way round and does not filter a row out: a command only the reader runs is exactly what the
* menu is for.
*
* **"On" is the effective answer**, not the stored switch: when the chat follows a mode, the
* mode's `tools.skills` patch applies over the user's `enabled` — the same rule the server's
* `assertInvocableSkills` and `listSkillsInForce` apply, read from
* `@openharness/protocol`'s `skillsInForce` so a menu row and the server cannot disagree. A
* menu built from `skill.enabled` alone offers a skill the server answers 400 for (the mode
* turned it off) and hides one the server would accept (the mode turned it on).
*
* The skills are listed in the order the caller passes them (the API's own order, oldest
* first), so two screens reading the same list build the same menu.
*
* @param skills the reader's skills, as `GET /v1/me/skills` answered — the stored `enabled`
* @param override the tool override of the mode the chat follows (`Mode.tools`), or `null`/absent
*   for a chat that follows none
*/
function slashCommands(skills, override = null) {
	return [...BUILTIN_COMMANDS.map((command) => ({
		name: command.name,
		kind: "builtin",
		description: command.description,
		usage: command.usage,
		argumentHint: null,
		skillId: null
	})), ...skillsInForce(skills, override).filter(isInvocableSkill).map((skill) => {
		const hint = skillSettingsOf(skill).argumentHint;
		return {
			name: skill.name,
			kind: "skill",
			description: skill.description,
			usage: hint === null ? `/${skill.name}` : `/${skill.name} <${hint}>`,
			argumentHint: hint,
			skillId: skill.id
		};
	})];
}
/**
* Whether a skill is one the reader can type: `user-invocable` (K1, K7).
*
* The on/off half is already answered by {@link skillsInForce} — and must be, because a mode
* may turn a stored-off skill on (K8), so reading `skill.enabled` here would undo the override.
*/
function isInvocableSkill(skill) {
	return skillSettingsOf(skill).userInvocable;
}
/**
* The word being typed after the `/`, or `null` when the box is not naming one.
*
* This is what a composer watches to open and filter the menu: `''` for a bare `/` (the menu
* opens showing everything), `'co'` for `/co`, and `null` once the box has moved past the name
* — `/compact keep the API decisions` is arguments, not a name — or is not a command line at
* all. A `/` anywhere but the first non-space character is not one either: a sentence that
* happens to contain a slash is a sentence.
*
* @param value the composer's current text
*/
function slashCommandQuery(value) {
	const trimmed = value.trimStart();
	if (!trimmed.startsWith("/")) return null;
	const token = trimmed.slice(1);
	if (/\s/.test(token)) return null;
	return token;
}
/**
* The rows a menu should show for what is being typed, in the order it shows them.
*
* A prefix match on the name, case-insensitively: `/` (`query` `''`) offers everything, `/co`
* offers `/compact`, and `/release` offers the skills whose names start that way. A query
* matching nothing answers an empty list, which is a menu that closes rather than a menu with a
* wrong row in it — the line is an ordinary message then, and the next keystroke decides.
*
* @param commands the whole menu, from {@link slashCommands}
* @param query the word after the `/`, from {@link slashCommandQuery}
*/
function filterSlashCommands(commands, query) {
	const needle = query.toLowerCase();
	return commands.filter((command) => command.name.toLowerCase().startsWith(needle));
}
/**
* Decide what Enter does with the line a reader has typed in a `/` menu's box (K7; #354).
*
* The one rule both frontends use, so `oh` and the web app cannot disagree about what a keypress
* does:
*
* - a **built-in** the box names **in full** is a command with nothing left to type (`/compact`,
*   and `/new`, `/mode` as their rows land), so Enter **runs** it rather than filling the box a
*   second time;
* - a **skill** is never run from the menu — `/name arguments` is sent as an ordinary message
*   carrying `skill` (K7) — so a row the reader is looking at is **completed** to `/name `, which
*   is what makes its `argument-hint` the box's placeholder and lets the arguments follow;
* - anything else — arguments already typed (`/name args`), a name nothing offers, or an ordinary
*   message — is **sent**, the behaviour the box always had.
*
* A line that only *starts* a name completes to the row the reader is on — the `highlighted`
* row, the one the arrows moved to — rather than to the first match.
*
* @param value the box's current text
* @param commands the whole menu, from {@link slashCommands}
* @param highlighted the row the menu is on, when it is up
*/
function slashEnterAction(value, commands, highlighted) {
	const query = slashCommandQuery(value);
	if (query === null) return { kind: "send" };
	const exact = commands.find((command) => command.name === query);
	if (exact !== void 0 && exact.kind === "builtin") return {
		kind: "run",
		name: exact.name
	};
	const row = highlighted ?? exact;
	return row === void 0 ? { kind: "send" } : {
		kind: "complete",
		name: row.name
	};
}
/**
* Read a composer line as one of the commands on offer, or as nothing.
*
* The one parse both frontends use before sending: a line that names a command is an
* invocation, and a line that names none is an ordinary message the caller sends as words.
* Only the exact spelling of a name is accepted — the whole first token, matched against the
* list — so a partially typed `/comp` is not `/compact`, and a message that opens with a slash
* something else wrote is not a command.
*
* @param value the composer's current text
* @param commands the whole menu, from {@link slashCommands}
*/
function parseSlashCommand(value, commands) {
	const trimmed = value.trim();
	const match = /^\/(\S+)(?:\s+([\s\S]*))?$/.exec(trimmed);
	if (match === null) return null;
	const name = match[1] ?? "";
	const command = commands.find((candidate) => candidate.name === name);
	if (command === void 0) return null;
	const rest = (match[2] ?? "").trim();
	return {
		text: trimmed,
		command,
		arguments: rest === "" ? null : rest
	};
}
/**
* The `skill` field a slash invocation rides on its `user.message`, or `null` for a built-in.
*
* A built-in command is a client action and never an invocation of a skill (K7), so it answers
* `null` — the caller runs the action instead of sending anything. A skill's arguments are
* omitted rather than sent as an empty string, so the brain substitutes `$ARGUMENTS` only when
* there were words to substitute (and appends nothing when there were none).
*/
function invocationSkill(invocation) {
	if (invocation.command.kind !== "skill") return null;
	return {
		name: invocation.command.name,
		...invocation.arguments === null ? {} : { arguments: invocation.arguments }
	};
}
/**
* Whether a built-in command name is one a skill may not take — the invariant between the menu
* and the reserved list, as a function so the test that holds them together reads as one.
*/
function isBuiltinCommandName(name) {
	return BUILTIN_COMMANDS.some((command) => command.name === name);
}
/**
* The badge a skill's row carries, or `null` for a skill with both halves available (K1).
*
* Two frontmatter fields change who can use a skill, and a reader looking at a list of them
* cannot see which — so each is named on the row: `command only` for
* `disable-model-invocation: true` (the model is never told it exists, K6) and `model only` for
* `user-invocable: false` (it is not a command they can type, K7). Read through
* `skillSettingsOf` so the words and the behaviour cannot disagree.
*/
function skillBadgeLabel(skill) {
	const settings = skillSettingsOf(skill);
	if (settings.disableModelInvocation) return "command only";
	if (!settings.userInvocable) return "model only";
	return null;
}
/**
* A short, stable rendering of a commit id: the first seven characters Git itself would print.
*
* @param sha a full commit sha, as a skill's source records it
*/
function shortCommit(sha) {
	return sha.slice(0, 7);
}
/**
* Where a skill came from, as one line: the URL, the ref and path it was taken at, and the
* commit it landed on (K3, K4).
*
* It is what a settings row shows under a skill's name — the URL is the reader's own input
* (they can re-run the same clone by hand from it), the ref and path are the two fields they
* had to open **Advanced** to name, and the commit is what the snapshot really is: a branch
* that moves does not change what was imported. A snapshot nothing cloned (a test's own skill)
* has no commit and reads as just the URL.
*/
function skillSourceLabel(source) {
	const parts = [source.url];
	if (source.ref !== null) parts.push(source.ref);
	if (source.path !== null) parts.push(source.path);
	const where = parts.join(" · ");
	return source.commit_sha === null ? where : `${where} @ ${shortCommit(source.commit_sha)}`;
}
/** A byte count as a reader reads it: `312 B`, `1.2 KB`, `2.4 MB`. */
function skillFileSizeLabel(size) {
	if (size < 1024) return `${size} B`;
	if (size < 1048576) return `${(size / 1024).toFixed(1)} KB`;
	return `${(size / 1048576).toFixed(1)} MB`;
}
/**
* Whether a previewed candidate can be imported as it stands (K3).
*
* Two things stop one, and they are kept apart by the protocol because they mean different
* things: the candidate's own `problems` (bad frontmatter, a name that is not the folder's, a
* reserved name, a limit of K4) and `name_taken` (nothing is wrong with the skill — the reader
* already has one of these). Either way the checkbox is off and cannot be turned on, because
* the import is refused rather than fixed by picking it.
*/
function candidateSelectable(candidate) {
	return candidate.problems.length === 0 && !candidate.name_taken;
}
/** The names of the candidates a preview offers that could really be imported. */
function importableCandidateNames(candidates) {
	return candidates.filter(candidateSelectable).map((candidate) => candidate.name);
}
/**
* Every file a refresh preview would change, in one list — added first, then removed, then
* changed, which is the order the protocol's three lists are read in.
*
* @param preview a refresh preview's answer
*/
function refreshFileChanges(preview) {
	return [
		...preview.files_added.map((path) => ({
			kind: "added",
			path
		})),
		...preview.files_removed.map((path) => ({
			kind: "removed",
			path
		})),
		...preview.files_changed.map((path) => ({
			kind: "changed",
			path
		}))
	];
}
/**
* What a refresh preview came to, in one sentence — `unchanged`, or what moved (K5).
*
* The count is the whole answer for a reader deciding whether to confirm: `2 files added, 1
* changed`. `SKILL.md` is not called out separately, because it is one of the files the three
* lists hold and the diff beside this sentence is where it is read.
*
* @param preview a refresh preview's answer
*/
function refreshChangeSummary(preview) {
	if (!preview.changed) return "unchanged";
	const parts = [
		[preview.files_added.length, "added"],
		[preview.files_removed.length, "removed"],
		[preview.files_changed.length, "changed"]
	].filter(([count]) => count > 0).map(([count, word]) => `${count} ${count === 1 ? "file" : "files"} ${word}`);
	return parts.length === 0 ? "the snapshot differs" : parts.join(", ");
}
/**
* The files of a skill or a candidate as one list a screen reads: each path and how big it is.
*
* A thin pass-through today, and here for the reason the rest of this module is: the two
* previews, the stored resource and both frontends all show "the files" and must show the same
* ones in the same order.
*/
function skillFileList(files) {
	return [...files].sort((left, right) => left.path < right.path ? -1 : left.path > right.path ? 1 : 0);
}
//#endregion
//#region src/approvals.ts
/**
* The calls a reader owes an answer to, in the order the model made them (epic #303, #310).
*
* A call waits when it is `waiting` — the status the transcript derives, which is a call whose
* `evaluated_permission` is `ask` and that no result has answered. That is the one reading, so a
* prompt is drawn from exactly the fact that makes the turn a pause.
*/
function pendingCalls(calls) {
	const pending = [];
	for (const call of calls) {
		const kind = pendingCallKind(call);
		if (kind === null) continue;
		pending.push({
			call,
			kind,
			questions: askUserQuestions(call) ?? []
		});
	}
	return pending;
}
/**
* Which prompt a waiting call needs, or `null` when it is not waiting (epic #303, #310).
*
* The name decides: a call to `ask_user` is a question, and every other waiting call is an
* approval. A call the log says is not waiting — one with a result, or one the policy ran
* outright — is no one's to answer.
*/
function pendingCallKind(call) {
	if (call.status !== "waiting") return null;
	return call.name === ASK_USER_TOOL_NAME ? "question" : "approval";
}
/**
* The questions an `ask_user` call asked, or `null` when it is not one or its input is not a
* well-formed set of questions.
*
* Parsed with the protocol's own {@link parseAskUserInput}, which is the same check the brain
* made before it paused — so a call that reaches a client as `ask_user` really does carry
* questions, and a malformed call the model made is one the brain answered with an error
* result rather than one a client has to render (that call is not waiting at all).
*/
function askUserQuestions(call) {
	if (call.name !== ASK_USER_TOOL_NAME) return null;
	const parsed = parseAskUserInput(call.input);
	return parsed === null ? null : parsed.questions;
}
/**
* The choices an approval prompt offers, in the order they are drawn — the safe one first.
*
* `allow-session` and `allow-always` are the `remember` extension the protocol carries on the
* confirmation: `session` allows every later call to the same tool in this chat (read back off
* the log, so an edit that rewinds past it takes it back), and `always` is the same **and**
* writes the reader's stored policy for that tool, which is what the next chat inherits.
*/
const APPROVAL_CHOICES = [
	{
		choice: "allow-once",
		label: "Allow once"
	},
	{
		choice: "allow-session",
		label: "Allow for this chat"
	},
	{
		choice: "allow-always",
		label: "Always allow"
	},
	{
		choice: "deny",
		label: "Deny"
	}
];
/** The words for one approval choice, the same in both frontends (epic #303, #310). */
function approvalChoiceLabel(choice) {
	return APPROVAL_CHOICES.find((entry) => entry.choice === choice)?.label ?? choice;
}
/**
* The sentence a prompt names the call it is about with (epic #303, #310; the MCP half, #312).
*
* A built-in is its own name — `web_fetch`. A remote tool is the name the model called it by
* **and the server it belongs to** (`notes__search (mcp: notes)`), because the decision in front
* of the reader is not only "which tool" but "whose": an MCP server is theirs, its answer is the
* server's, and two servers may both offer a `search`. {@link mcpServerMarker} is the one
* spelling of the second half, shared with the call line, so the prompt and the line above it
* say the same thing about the same call.
*
* @param call the waiting call — its name, where it comes from, and the server when it is a
*   remote one
*/
function pendingCallHeading(call) {
	return call.source === "mcp" ? `${call.name} (${mcpServerMarker(call.server)})` : call.name;
}
/**
* What `remember` a choice sends, or `undefined` for the `once` the protocol defaults to.
*
* `allow-once` is `undefined` rather than the literal `'once'` because the field is an
* extension: leaving it out is what a confirmation that means "this call and no other" says,
* and it keeps an approval of a call made by an older client byte-identical.
*/
function rememberFor(choice) {
	switch (choice) {
		case "allow-session": return "session";
		case "allow-always": return "always";
		default: return;
	}
}
/** The `result` a choice sends: only `deny` refuses the call. */
function resultFor(choice) {
	return choice === "deny" ? "deny" : "allow";
}
/**
* The event that answers one waiting call with an approval or a refusal (epic #303, X6; #310).
*
* A denial may carry the reader's own words, which the brain turns into the result the model
* reads (`The user denied this: …`) — an empty or whitespace-only message is left out rather
* than sent blank, because the protocol wants a non-empty one.
*
* @param toolUseId the call's id — the `agent.tool_use` event's own id
* @param choice what the reader decided
* @param denyMessage why, when it was a denial and the reader said so
*/
function approvalConfirmation(toolUseId, choice, denyMessage) {
	const remember = rememberFor(choice);
	const message = denyMessage?.trim();
	return {
		type: "user.tool_confirmation",
		tool_use_id: toolUseId,
		result: resultFor(choice),
		...choice === "deny" && message !== void 0 && message !== "" ? { deny_message: message } : {},
		...remember === void 0 ? {} : { remember }
	};
}
/**
* The events that answer several waiting calls the same way (epic #303, #310).
*
* "Allow all" and "Deny all" are one event per call — the log holds one confirmation per call,
* which is what makes "every confirmation names a call that was waiting" readable one call at
* a time — sent in one batch. The calls are answered in the order they were made, so the
* results the brain writes read like the model's own questions.
*/
function approvalConfirmations(calls, choice) {
	return calls.map((call) => approvalConfirmation(call.id, choice));
}
/**
* The event that refuses a call the reader will not answer (epic #303, #310).
*
* This is what an `ask_user` prompt's **Decline** sends: a question with no answers is not an
* approval, and a denial is the one event that says "not answered" — the brain writes it as an
* `is_error` result, so the model learns the reader moved on rather than reading silence.
*/
function declineConfirmation(toolUseId, message) {
	return approvalConfirmation(toolUseId, "deny", message);
}
/**
* The event that carries an `ask_user` call's answers (epic #303, X6; #310).
*
* The answers **are** the call's result — the tool never runs — so this is an approval with
* `answers` rather than a result of its own. The protocol refuses a question approved with no
* answers, which is why {@link draftProblems} has to be clear before a form submits.
*/
function answerConfirmation(toolUseId, answers) {
	return {
		type: "user.tool_confirmation",
		tool_use_id: toolUseId,
		result: "allow",
		answers
	};
}
/**
* The label a `choice` question always offers whatever its options are (epic #303, X6; #310).
*
* "Other" with free text is part of the type rather than an option a call lists — a model that
* wants a write-in does not have to ask for one — so both forms draw this row themselves and
* an answer that carries only `text` is the write-in.
*/
const OTHER_CHOICE_LABEL = "Other";
/** The draft a question starts from: nothing said. */
function emptyDraft() {
	return {
		labels: [],
		text: "",
		other: false,
		confirmed: null
	};
}
/** The drafts a form starts from: one per question, in the order they were asked. */
function emptyDrafts(questions) {
	return questions.map(() => emptyDraft());
}
/**
* Select or deselect an option of a `choice` question.
*
* A single-select question replaces what was chosen; a `multi_select` one toggles, so a reader
* can pick several and unpick one. Choosing an option never touches the write-in: they are
* sent side by side, which is exactly what an answer carrying both `labels` and `text` means.
*/
function withLabel(draft, label, options) {
	if (!options.multi) return {
		...draft,
		labels: [label]
	};
	return draft.labels.includes(label) ? {
		...draft,
		labels: draft.labels.filter((chosen) => chosen !== label)
	} : {
		...draft,
		labels: [...draft.labels, label]
	};
}
/** Turn the write-in row on or off, keeping whatever the reader has typed so far. */
function withOther(draft, other) {
	return {
		...draft,
		other
	};
}
/** Set the reader's own words, for a `text` question or a write-in. */
function withText(draft, text) {
	return {
		...draft,
		text
	};
}
/** Answer a `confirm` question. */
function withConfirmed(draft, confirmed) {
	return {
		...draft,
		confirmed
	};
}
/**
* The drafts as the wire's answers, one per question (epic #303, #310).
*
* Fields a question's type does not carry are left out rather than sent empty — a `choice`
* with no label and no write-in sends neither, which is what makes the server's validator see
* the answer a reader really gave. Callers should have checked {@link draftProblems} first;
* this builds the shape and does not decide whether it fits.
*
* @param questions the questions the call asked, in order
* @param drafts one draft per question, by index
*/
function answersFrom(questions, drafts) {
	return questions.map((question, index) => {
		const draft = drafts[index] ?? emptyDraft();
		const text = draft.text.trim();
		if (question.type === "confirm") return {
			question: question.question,
			...draft.confirmed === null ? {} : { confirmed: draft.confirmed }
		};
		if (question.type === "text") return {
			question: question.question,
			...text === "" ? {} : { text }
		};
		return {
			question: question.question,
			...draft.labels.length === 0 ? {} : { labels: draft.labels },
			...draft.other && text !== "" ? { text } : {}
		};
	});
}
/**
* What is wrong with the drafts, one `header: message` per problem — empty when they can be
* sent (epic #303, X6; #310).
*
* The check is the protocol's own {@link askUserAnswerProblems}, so a form is held to exactly
* the shape the server refuses a 400 for — the reader sees why next to the question rather
* than the whole submission coming back as an error. A caller disables Submit on a non-empty
* answer and shows the lines beside the questions.
*/
function draftProblems(questions, drafts) {
	return askUserAnswerProblems({ questions: [...questions] }, answersFrom(questions, drafts));
}
/** Whether every question is answered well enough to submit ({@link draftProblems} is empty). */
function draftComplete(questions, drafts) {
	return draftProblems(questions, drafts).length === 0;
}
/**
* What an answered pause shows in the transcript, or `null` when it needs no line of its own
* (epic #303, X6; #310).
*
* The `user.tool_confirmation` is the log's record of the decision, and the transcript keeps
* it so a call that ran can say **how** it was allowed — "Allowed once", "Allowed for this
* chat", "Always allowed" — which the tool's own result cannot: a result is what the tool
* answered, not what the reader decided. A denial is drawn from the result the brain wrote
* (the reader's own words are in it), and an `ask_user` call's answers **are** its result, so
* both answer `null`: the call line already says them.
*/
function confirmationSummary(confirmation) {
	if (confirmation.result === "deny") return null;
	if (confirmation.answers !== void 0) return null;
	switch (confirmation.remember) {
		case "session": return "Allowed for this chat";
		case "always": return "Always allowed";
		default: return "Allowed once";
	}
}
/**
* The line a reader is owed while calls are waiting and they are typing a message instead
* (epic #303, #310).
*
* A `user.message` that arrives while a turn waits resolves every waiting call — the brain
* answers each `The user sent a message instead.` — so sending one is a decision about all of
* them, and the composer says so before the reader presses Enter rather than after. The count
* is what makes it honest when several calls are waiting at once.
*
* @param count how many calls are waiting on the reader
*/
function pendingCallsNotice(count) {
	if (count <= 0) return null;
	return count === 1 ? "Sending a message will decline the item waiting on you." : `Sending a message will decline the ${count} items waiting on you.`;
}
//#endregion
//#region src/stall.ts
/**
* Whether a running turn has gone quiet for long enough to tell the reader (#378, P4).
*
* A crash is not always visible. When the owner of a streaming turn dies, the client keeps its
* connection and its transcript, and nothing arrives until another instance notices the lapsed
* lease, closes the orphaned span and re-runs the request — about a lease TTL plus a heartbeat,
* 40 s with the deployment's defaults. Without a word from the screen that is indistinguishable
* from a model that is thinking hard.
*
* The rule is deliberately **time-based and outside the transcript reducer**. The log holds no
* wall clock, and the reducer is pure by design: replaying the same events must produce the
* same state. So the frontends keep when the last event arrived beside the transcript (the way
* the TUI already keeps the turn's clock) and call this function with their own `now`.
*
* `lastEventAt` is the arrival time of the last event **this client folded in**, not the
* event's `processed_at`: a resumed stream's backlog may be old, and the question is whether
* the screen has heard anything lately.
*
* The threshold is the server's SSE keep-alive cadence (15 s, `apps/server/src/sse.ts`). A
* keep-alive proves the socket is alive, but it carries no session event, so it does not reset
* this clock: the indicator says "the turn is running and nothing has arrived", which is what a
* reader sees. A connection that is really reconnecting is a different and louder fact, and
* `reconnecting` outranks the clock — the client's reconnect loop reports it as it happens.
*/
const DEFAULT_STALL_MS = 15e3;
/**
* What to tell the reader about a running turn, from the transcript's status and the time the
* last event arrived.
*
* A turn that is not running never stalls — a pause, a task wait and a finished turn are all
* idle and are waiting on something the log can say. A client that has not seen an event yet
* has no baseline to measure from and says nothing either; a caller loading history sets
* `lastEventAt` when it starts following, so a resumed session is measured from then.
*/
function stallState(input) {
	if (input.reconnecting === true) return "reconnecting";
	if (input.status !== "running" || input.lastEventAt === null || input.lastEventAt === void 0) return "ok";
	const threshold = input.thresholdMs ?? 15e3;
	return input.now - input.lastEventAt >= threshold ? "stalled" : "ok";
}
//#endregion
//#region src/task-activity.ts
/** The state of a child nothing has read yet: nothing running, nothing spent. */
const NOTHING_YET = {
	activity: { kind: "idle" },
	waiting: false,
	status: "idle",
	model: null,
	usage: null,
	cost: null
};
/**
* What a child's transcript says its activity is, as the card draws it (issue #345).
*
* **Who calls this.** `watchTaskActivity` is the production reader — it folds the child's stream
* into a transcript and derives a snapshot with this function after every event — and both
* frontends go through the watcher rather than calling this directly, because a card needs a live
* line and not a derivation someone recomputes. What is left is the **test** seam the review of
* #367 asked to have named: a test that wants to hold a snapshot still, or to check what a log
* derives to without a stream, calls this with a transcript it built itself
* (`apps/tui/src/components/task-card.test.tsx` does exactly that).
*
* The rules, in the order they are tried:
*
* 1. **a running tool call** — the newest call with no result and a turn that is working — is
*    the child's current work, drawn as its name and the shared summary;
* 2. else **the task list**, when the model has written one: "2 of 5", the count the todo panel
*    draws of the same list;
* 3. else **"writing…"** while the child's turn is running, because a model that is streaming a
*    reply and calling nothing is writing one;
* 4. else **nothing** — `idle`, which a card draws no line for.
*
* The tokens are the child's own usage, reported the same way the child's chat header would
* report them, and the cost is priced with the caller's lookup; both are `null` while the
* child's log has said nothing.
*
* @param state the child's transcript
* @param options see {@link TaskActivityOptions}
*/
function taskActivityOf(state, options = {}) {
	const usage = selectSessionUsage(state);
	const totals = usage.models.length === 0 ? null : usage.totals;
	return {
		activity: activityLine(state),
		waiting: options.waiting === true,
		status: state.status,
		model: state.model,
		usage: totals,
		cost: totals === null || options.prices === void 0 ? null : sessionCost(usage, options.prices)
	};
}
/**
* The words a card's activity line is drawn with, or `null` when there is nothing to say
* (issue #345).
*
* Both frontends draw the same sentence, so it is spelled once — a tool as `name: summary`
* (or the bare name when the input has nothing short), a task list as `2 of 5`, and a model
* that is writing as `writing…`. `idle` answers `null`: a card with nothing to report draws no
* line rather than an empty one.
*
* @param activity the line {@link taskActivityOf} derived
*/
function taskActivityLabel(activity) {
	switch (activity.kind) {
		case "tool": return activity.summary === null ? activity.name : `${activity.name}: ${activity.summary}`;
		case "todos": return `${activity.done} of ${activity.total}`;
		case "writing": return "writing…";
		case "idle": return null;
	}
}
/** The activity line a child's transcript implies — see {@link taskActivityOf} for the rules. */
function activityLine(state) {
	const call = runningCall(state);
	if (call !== null) return {
		kind: "tool",
		name: call.name,
		summary: toolCallSummary(call)
	};
	const todos = state.todos;
	if (todos !== null && todos.length > 0) return {
		kind: "todos",
		done: completedCount(todos),
		total: todos.length
	};
	return state.status === "running" ? { kind: "writing" } : { kind: "idle" };
}
/** The newest call the child has out, or `null` when none is. */
function runningCall(state) {
	for (let index = state.toolCalls.length - 1; index >= 0; index -= 1) {
		const call = state.toolCalls[index];
		if (call?.status === "running") return call;
	}
	return null;
}
/** How many of a list's items are done — the `N` of "N of M". */
function completedCount(todos) {
	return todos.filter((item) => item.status === "done").length;
}
/**
* Follow one child session and answer what it is doing (epic #340, S9; issue #345).
*
* ```ts
* const activity = watchTaskActivity(client.sessions.events, card.childSessionId, { prices })
* activity.subscribe(() => render(activity.getState()))
* // … and `close()`, or the last unsubscribe, drops the connection.
* ```
*
* The stream is the existing one (`events/stream`, reconnecting and resuming by `seq`), read
* after the child's history so a card opened on a child that is already working shows its
* current call rather than waiting for the next one. A failure to read or follow the child is
* reported through `onDebug` and ends the follower: a card that cannot say what its child is
* doing is a card with no activity line, never a chat that stops working.
*
* @param log the child's log — `client.sessions.events`, or a double in a test
* @param sessionId the child's `sesn_` id, from {@link TaskCard.childSessionId}
* @param options the catalog's prices and the debug hook; see
*   {@link TaskActivityWatchOptions}
*/
function watchTaskActivity(log, sessionId, options = {}) {
	const transcript = createTranscript();
	const listeners = /* @__PURE__ */ new Set();
	let waiting = false;
	let snapshot = NOTHING_YET;
	let controller = null;
	const refresh = () => {
		const next = taskActivityOf(transcript.getState(), {
			waiting,
			...options.prices === void 0 ? {} : { prices: options.prices }
		});
		if (isSameActivity(snapshot, next)) return;
		snapshot = next;
		for (const listener of listeners) listener(snapshot);
	};
	const apply = (event) => {
		transcript.apply(event);
		if (event.type === EVENT_TYPES.sessionStatusIdle) waiting = event.stop_reason.type === "waiting_on_tasks";
		else if (event.type === EVENT_TYPES.sessionStatusRunning || event.type === EVENT_TYPES.sessionStatusRescheduled) waiting = false;
		refresh();
	};
	const follow = async (signal) => {
		try {
			for await (const event of log.iterate(sessionId, void 0, { signal })) {
				if (signal.aborted) return;
				apply(event);
			}
			for await (const event of log.stream(sessionId, {
				afterSeq: transcript.getState().lastSeq,
				signal
			})) {
				if (signal.aborted) return;
				apply(event);
			}
		} catch (error) {
			if (!signal.aborted) options.onDebug?.("stopped following a task: its session could not be read", error);
		} finally {
			if (controller?.signal === signal) controller = null;
		}
	};
	const start = () => {
		if (controller !== null) return;
		const next = new AbortController();
		controller = next;
		follow(next.signal);
	};
	const stop = () => {
		const current = controller;
		controller = null;
		current?.abort();
	};
	return {
		getState: () => snapshot,
		subscribe(listener) {
			listeners.add(listener);
			start();
			return () => {
				listeners.delete(listener);
				if (listeners.size === 0) stop();
			};
		},
		close() {
			listeners.clear();
			stop();
		}
	};
}
/** Whether two snapshots say the same thing, so a watcher can keep the object it had. */
function isSameActivity(current, next) {
	return current.waiting === next.waiting && current.status === next.status && current.model === next.model && isSameLine(current.activity, next.activity) && isSameTotals(current.usage, next.usage) && current.cost?.cost === next.cost?.cost && current.cost?.unpriced_requests === next.cost?.unpriced_requests;
}
/** Whether two activity lines are the same shape with the same words. */
function isSameLine(current, next) {
	if (current.kind !== next.kind) return false;
	switch (current.kind) {
		case "tool": return next.kind === "tool" && current.name === next.name && current.summary === next.summary;
		case "todos": return next.kind === "todos" && current.done === next.done && current.total === next.total;
		default: return true;
	}
}
/** Whether two token totals are the same, counting `null` as "nothing reported yet". */
function isSameTotals(current, next) {
	if (current === null || next === null) return current === next;
	return current.input === next.input && current.output === next.output && current.cacheCreation === next.cacheCreation && current.cacheRead === next.cacheRead;
}
//#endregion
//#region src/session-tree.ts
/**
* The children of a session, newest first — the whole list, not its first page.
*
* `GET /v1/sessions?parent_session_id=` answers one page at a time, so this walks the cursor
* the way the events list does — the cursor is opaque and handed back byte for byte — and
* stops on a cursor the server has already given (a server that repeats itself would otherwise
* page for ever).
*
* An empty array means the session has no children: the nested sidebar has nothing to hang
* under it, and the card list is the whole of what a task started.
*
* @param sessions the client's sessions resource, or a double
* @param sessionId the parent's `sesn_` id
* @param options request options (cancellation)
*/
async function listChildren(sessions, sessionId, options) {
	const children = [];
	const requested = /* @__PURE__ */ new Set();
	let page;
	for (;;) {
		const response = await sessions.list({
			parent_session_id: sessionId,
			limit: MAX_PAGE_LIMIT,
			...page === void 0 ? {} : { page }
		}, options);
		const next = response.next_page;
		if (next !== null && requested.has(next)) return children;
		children.push(...response.data);
		if (next === null) return children;
		requested.add(next);
		page = next;
	}
}
/**
* The chain from a session up to the top-level chat it hangs off, **root first**.
*
* The breadcrumb a child's own screen draws ("back to the parent", and each step above it):
* the array opens with the root — the chat a reader started — and ends with the session
* itself, so a frontend renders it in order and links every step but the last.
*
* The session is passed in rather than read, because the caller has it: a frontend that opened
* a child holds the read it opened it with. Each step above is one `GET`, and the walk stops
* when a session has no parent — or when it meets one it has already seen, because a server
* that answered a cycle would otherwise loop for ever, and a breadcrumb is not worth a hang.
*
* @param sessions the client's sessions resource, or a double
* @param session the session to walk up from
* @param options request options (cancellation)
*/
async function sessionBreadcrumb(sessions, session, options) {
	const chain = [session];
	const seen = /* @__PURE__ */ new Set([session.id]);
	let current = session;
	while (current.parent_session_id !== null) {
		const parentId = current.parent_session_id;
		if (seen.has(parentId)) break;
		const parent = await sessions.get(parentId, options);
		seen.add(parent.id);
		chain.push(parent);
		current = parent;
	}
	return chain.reverse();
}
//#endregion
//#region src/index.ts
/**
* `@openharness/client` — the typed SDK for the openharness API, for browsers and for Node.
*
* Five things live here:
*
* - **the client** — {@link createClient}, and the `Client` interface it and the fake client
*   both implement: agents, sessions, the session event log, the provider credentials, the
*   caller's preferences, usage and cost, the auth helpers, and helpers for sending a message
*   (optionally switching the model) and interrupting a turn;
* - **streaming** — `client.sessions.events.stream`, an async iterable of `StreamEvent`s that
*   reconnects and resumes by `seq`, so no stored event is delivered twice or skipped;
* - **the transcript** — {@link createTranscript}, a pure reducer from those events to UI
*   state, shared by the web app and the TUI;
* - **authentication** — a cookie in the browser and a bearer token (the `token` option) for
*   the CLI, plus the device-code flow ({@link OPENHARNESS_CLI_CLIENT_ID}) `oh login` runs;
* - **errors** — {@link ApiError} for a non-2xx answer, built from the protocol's error
*   envelope, with {@link AuthenticationError} (401) as its own type.
*
* Everything crossing the boundary is typed by `@openharness/protocol`, which owns the wire
* contract; this package adds I/O, not schemas. The one exception is Better Auth's
* `/api/auth/*` surface, which is not part of the protocol and is typed here.
*
* `@openharness/client/testing` has `createFakeClient()` — the same interface, backed by an
* in-memory server, with a scriptable brain, credentials and device flow.
*/
/** This package's name; lets a dependent prove the import resolved. */
const PACKAGE_NAME = "@openharness/client";
//#endregion
export { APPROVAL_CHOICES, ApiError, AuthenticationError, BUILTIN_COMMANDS, CHARS_PER_TOKEN, COMPACTING_LABEL, CREDENTIAL_TARGETS, DEFAULT_COMPACTION_THRESHOLD, DEFAULT_CONTEXT_TOKEN_BUDGET, DEFAULT_STALL_MS, DeviceLoginError, MCP_AUTH_TYPES, MCP_CONNECTED_PARAM, MCP_CONNECT_POLL_INTERVAL_MS, MCP_CONNECT_TIMEOUT_MS, NOT_REGISTERED_NOTE, NO_ASK_TOOL_NAMES, OPENHARNESS_CLI_CLIENT_ID, OTHER_CHOICE_LABEL, OUTPUT_RESERVE_RATIO, PACKAGE_NAME, PROVIDERS, ResponseValidationError, SUBAGENT_ASK_NOTE, TOOLS_UNSUPPORTED_NOTICE, TOOL_LOOP_STUCK_NOTICE, TOOL_POLICIES, TOOL_STEPS_EXHAUSTED_NOTICE, answerConfirmation, answersFrom, approvalChoiceLabel, approvalConfirmation, approvalConfirmations, askUserQuestions, candidateSelectable, clearedResultsNotice, compactionThreshold, confirmationSummary, contextAfterSummary, contextMeter, contextTokenBudget, createClient, createTranscript, credentialDisplayName, credentialFacts, credentialRowLabel, credentialTargetFor, cycleToolPolicy, declineConfirmation, draftComplete, draftProblems, emptyDraft, emptyDrafts, errorTypeForStatus, estimateTokens, estimatedTokensLabel, filterSlashCommands, formatToolInput, importableCandidateNames, initialTranscriptState, invocationSkill, isBuiltinCommandName, isTaskToolName, listChildren, manualCompactionNotice, mcpAuthHint, mcpAuthLabel, mcpConnectedServerId, mcpFailureNotice, mcpServerById, mcpServerByName, mcpServerFromError, mcpServerMarker, mcpServerPolicyBody, mcpServerTools, mcpStatusDetail, mcpStatusLabel, mcpStatusTone, mcpTestOutcome, mcpToolEntriesByServer, mcpToolPolicyBody, mcpToolPolicyState, mcpToolPolicyStateLabel, modelContextBudget, modelSupportsTools, parseSlashCommand, pendingCallHeading, pendingCallKind, pendingCalls, pendingCallsNotice, providerInfo, providerName, reduceTranscript, reduceTranscriptAll, refreshChangeSummary, refreshFileChanges, replyCost, searchCount, selectClearedToolResults, selectConfirmation, selectConfirmations, selectContext, selectIsRunning, selectLastMessage, selectManualCompaction, selectMcpFailure, selectMessages, selectSearchCount, selectSessionUsage, selectStreamingMessage, selectSummaries, selectSummarizing, selectTasks, selectTodos, selectToolCalls, selectTranscriptEntries, selectTruncatedToolResults, selectTruncation, sessionBreadcrumb, sessionCost, sessionUsageOf, shortCommit, skillBadgeLabel, skillFileList, skillFileSizeLabel, skillSourceLabel, skillToolCallLabel, slashCommandQuery, slashCommands, slashEnterAction, stallState, stepLimitNotice, stuckNotice, summaryDescription, summaryModelFallback, taskActivityLabel, taskActivityOf, taskCardStatus, taskIsLive, tasksFromEvents, toolAvailabilityNote, toolCallStatus, toolCallSummary, toolPolicyChoices, toolPolicyLabel, toolPolicyNote, toolStatusLabel, toolSupportsAsk, transcriptEntries, truncatedResultsNotice, truncationNotice, waitForMcpConnection, watchTaskActivity, withConfirmed, withLabel, withOther, withText };

//# sourceMappingURL=index.js.map